/**
 * ARTFIT Shopify Theme - Complete JavaScript
 * No external dependencies. Uses Shopify AJAX API.
 */

(function () {
  'use strict';

  /* ==========================================================================
     1. CORE UTILITIES
     ========================================================================== */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function delegate(container, selector, event, handler) {
    container.addEventListener(event, (e) => {
      const el = e.target.nodeType === Node.ELEMENT_NODE ? e.target : e.target.parentElement;
      if (!el) return;
      const target = el.closest(selector);
      if (target && container.contains(target)) handler(e, target);
    });
  }

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  function throttle(fn, limit) {
    let inThrottle = false;
    return (...args) => {
      if (!inThrottle) {
        fn(...args);
        inThrottle = true;
        setTimeout(() => { inThrottle = false; }, limit);
      }
    };
  }

  // Adaptive image background — dense edge sampling with OKLAB gradients.
  // Samples many points along each edge, builds multi-stop gradients that
  // precisely match the image boundary colors.
  var _adaptiveBgCache = new Map();

  function adaptiveImageBg(img, container) {
    if (!img || !container) return;
    var S = 64;

    var apply = function () {
      try {
        var src = img.src || img.currentSrc;
        if (src && _adaptiveBgCache.has(src)) {
          container.style.background = _adaptiveBgCache.get(src);
          return;
        }

        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = S; canvas.height = S;
        ctx.drawImage(img, 0, 0, S, S);
        var data = ctx.getImageData(0, 0, S, S).data;

        function px(x, y) {
          x = Math.max(0, Math.min(S-1, x));
          y = Math.max(0, Math.min(S-1, y));
          var i = (y * S + x) * 4;
          return [data[i], data[i+1], data[i+2], data[i+3]];
        }

        // ── OKLAB ──
        function s2l(x) { return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); }
        function l2s(x) { return x <= 0.0031308 ? x * 12.92 : 1.055 * Math.pow(x, 1/2.4) - 0.055; }
        function toOk(r, g, b) {
          var lr = s2l(r/255), lg = s2l(g/255), lb = s2l(b/255);
          var l = Math.cbrt(.4122214708*lr+.5363325363*lg+.0514459929*lb);
          var m = Math.cbrt(.2119034982*lr+.6806995451*lg+.1073969566*lb);
          var s = Math.cbrt(.0883024619*lr+.2817188376*lg+.6299787005*lb);
          return [.2104542553*l+.7936177850*m-.0040720468*s, 1.9779984951*l-2.4285922050*m+.4505937099*s, .0259040371*l+.7827717662*m-.8086757660*s];
        }
        function fromOk(L, a, b) {
          var l=L+.3963377774*a+.2158037573*b, m=L-.1055613458*a-.0638541728*b, s=L-.0894841775*a-1.2914855480*b;
          l=l*l*l; m=m*m*m; s=s*s*s;
          return [Math.round(Math.min(255,Math.max(0,l2s(4.0767416621*l-3.3077115913*m+.2309699292*s)*255))),
                  Math.round(Math.min(255,Math.max(0,l2s(-1.2684380046*l+2.6097574011*m-.3413193965*s)*255))),
                  Math.round(Math.min(255,Math.max(0,l2s(-.0041960863*l-.7034186147*m+1.7076147010*s)*255)))];
        }
        function blendOk(c1, c2, t) {
          var a = toOk(c1[0],c1[1],c1[2]), b = toOk(c2[0],c2[1],c2[2]);
          return fromOk(a[0]+(b[0]-a[0])*t, a[1]+(b[1]-a[1])*t, a[2]+(b[2]-a[2])*t);
        }

        // ── HSL for saturation/lightness clamping ──
        function toHsl(r,g,b) {
          r/=255;g/=255;b/=255;var mx=Math.max(r,g,b),mn=Math.min(r,g,b),h,s,l=(mx+mn)/2;
          if(mx===mn){h=s=0;}else{var d=mx-mn;s=l>.5?d/(2-mx-mn):d/(mx+mn);
          if(mx===r)h=((g-b)/d+(g<b?6:0))/6;else if(mx===g)h=((b-r)/d+2)/6;else h=((r-g)/d+4)/6;}
          return[h,s,l];
        }
        function fromHsl(h,s,l) {
          if(s===0){var v=Math.round(l*255);return[v,v,v];}
          function h2(p,q,t){if(t<0)t+=1;if(t>1)t-=1;if(t<1/6)return p+(q-p)*6*t;if(t<.5)return q;if(t<2/3)return p+(q-p)*(2/3-t)*6;return p;}
          var q=l<.5?l*(1+s):l+s-l*s,p=2*l-q;
          return[Math.round(h2(p,q,h+1/3)*255),Math.round(h2(p,q,h)*255),Math.round(h2(p,q,h-1/3)*255)];
        }

        // ── Helpers ──
        function wavg(colors) {
          var r=0,g=0,b=0,n=colors.length;
          if(!n)return[245,245,245];
          for(var i=0;i<n;i++){r+=colors[i][0];g+=colors[i][1];b+=colors[i][2];}
          return[Math.round(r/n),Math.round(g/n),Math.round(b/n)];
        }
        var rgb = function(c) { return 'rgb('+c[0]+','+c[1]+','+c[2]+')'; };
        var rgba = function(c, a) { return 'rgba('+c[0]+','+c[1]+','+c[2]+','+a+')'; };

        // Tame a color: cap saturation, keep lightness in range
        function tame(c) {
          var h = toHsl(c[0],c[1],c[2]);
          var sat = Math.min(h[1], 0.45);
          var lit = h[2];
          if (lit < 0.32) lit = 0.32 + lit * 0.35;
          if (lit > 0.88) lit = 0.86;
          return fromHsl(h[0], sat, lit);
        }

        // Sample a strip along an edge, averaging 3px deep
        // Returns array of N colors evenly spaced along the edge
        function sampleEdgeStrip(edge, N) {
          var colors = [];
          for (var s = 0; s < N; s++) {
            var t = (s + 0.5) / N;
            var samples = [];
            for (var d = 0; d < 3; d++) {
              var x, y;
              if (edge === 'top')    { x = Math.round(t * (S-1)); y = d; }
              else if (edge === 'bottom') { x = Math.round(t * (S-1)); y = S-1-d; }
              else if (edge === 'left')   { x = d; y = Math.round(t * (S-1)); }
              else                         { x = S-1-d; y = Math.round(t * (S-1)); }
              var c = px(x, y);
              if (c[3] >= 128) samples.push([c[0], c[1], c[2]]);
            }
            colors.push(samples.length ? wavg(samples) : [245, 245, 245]);
          }
          return colors;
        }

        // ── Dense edge sampling: 8 points per edge ──
        var N = 8;
        var topStrip = sampleEdgeStrip('top', N);
        var botStrip = sampleEdgeStrip('bottom', N);
        var leftStrip = sampleEdgeStrip('left', N);
        var rightStrip = sampleEdgeStrip('right', N);

        // Overall average from all edges
        var allEdge = [];
        for (var ei = 0; ei < N; ei++) {
          allEdge.push(topStrip[ei], botStrip[ei], leftStrip[ei], rightStrip[ei]);
        }
        var overall = wavg(allEdge);

        // ── Transparency check ──
        var alphaCount = 0;
        for (var ai = 0; ai < S; ai += 3) {
          if (px(ai, 0)[3] < 128) alphaCount++;
          if (px(ai, S-1)[3] < 128) alphaCount++;
          if (px(0, ai)[3] < 128) alphaCount++;
          if (px(S-1, ai)[3] < 128) alphaCount++;
        }
        if (alphaCount / (Math.ceil(S/3) * 4) > 0.25) {
          var bgVal = '#fafafa';
          container.style.background = bgVal;
          if (src) _adaptiveBgCache.set(src, bgVal);
          return;
        }

        // ── Studio shot detection ──
        var ringColors = [];
        var rD = Math.floor(S * .12);
        for (var rx = 0; rx < S; rx += 3) for (var ry = 0; ry < S; ry += 3) {
          if (rx < rD || rx >= S-rD || ry < rD || ry >= S-rD) {
            var rp = px(rx, ry);
            if (rp[3] >= 128) ringColors.push([rp[0], rp[1], rp[2]]);
          }
        }
        var isStudio = false, studioBg = overall;
        if (ringColors.length > 10) {
          var oA = wavg(ringColors), oV = 0;
          for (var oi = 0; oi < ringColors.length; oi++)
            for (var oc = 0; oc < 3; oc++) { var od = ringColors[oi][oc]-oA[oc]; oV += od*od; }
          oV = Math.sqrt(oV / (ringColors.length * 3));
          if (oV < 10) { isStudio = true; studioBg = oA; }
        }

        // ── Luminance ──
        var lum = .299*overall[0] + .587*overall[1] + .114*overall[2];
        var isLight = lum > 235;

        var bg;

        if (isStudio) {
          var sN = tame(studioBg);
          bg = 'radial-gradient(ellipse at center, '+rgb(blendOk(sN,[255,255,255],.06))+' 0%, '+rgb(sN)+' 100%)';

        } else if (isLight) {
          bg = 'radial-gradient(ellipse at center, '+rgb(blendOk(overall,[255,255,255],.3))+' 0%, '+rgb(blendOk(overall,[248,246,243],.5))+' 100%)';

        } else {
          // ── Build multi-stop gradients from dense edge samples ──
          // Each edge gets a gradient with exact colors at each sample point.
          // Fade from full opacity at the edge to transparent toward center.

          // Tame all strip colors
          var tTop = [], tBot = [], tLeft = [], tRight = [];
          for (var ti = 0; ti < N; ti++) {
            tTop.push(tame(blendOk(topStrip[ti], overall, 0.15)));
            tBot.push(tame(blendOk(botStrip[ti], overall, 0.15)));
            tLeft.push(tame(blendOk(leftStrip[ti], overall, 0.15)));
            tRight.push(tame(blendOk(rightStrip[ti], overall, 0.15)));
          }

          // Letterbox detection
          var imgR = img.naturalWidth / img.naturalHeight;
          var cRect = container.getBoundingClientRect();
          var cR = cRect.width && cRect.height ? cRect.width / cRect.height : imgR;
          var lbH = imgR - cR < -0.15;
          var lbV = imgR - cR > 0.15;
          var fadeH = lbV ? 50 : 38;  // horizontal fade for top/bottom
          var fadeV = lbH ? 50 : 38;  // vertical fade for left/right

          // For each edge: one linear fade + radial spots per sample point

          var layers = [];

          // 4 edge fades using per-segment average
          var avgTop = tame(wavg(topStrip));
          var avgBot = tame(wavg(botStrip));
          var avgLeft = tame(wavg(leftStrip));
          var avgRight = tame(wavg(rightStrip));

          layers.push('linear-gradient(to bottom, '+rgba(avgTop,.88)+' 0%, transparent '+fadeH+'%)');
          layers.push('linear-gradient(to top, '+rgba(avgBot,.88)+' 0%, transparent '+fadeH+'%)');
          layers.push('linear-gradient(to right, '+rgba(avgLeft,.82)+' 0%, transparent '+fadeV+'%)');
          layers.push('linear-gradient(to left, '+rgba(avgRight,.82)+' 0%, transparent '+fadeV+'%)');

          // Per-sample radial spots along each edge for color variation
          for (var si = 0; si < N; si++) {
            var pct = ((si + 0.5) / N * 100).toFixed(1);
            var spotSize = (100 / N + 8).toFixed(0);

            // Top edge spots
            layers.push('radial-gradient(ellipse at '+pct+'% 0%, '+rgba(tTop[si],.55)+' 0%, transparent '+spotSize+'%)');
            // Bottom edge spots
            layers.push('radial-gradient(ellipse at '+pct+'% 100%, '+rgba(tBot[si],.55)+' 0%, transparent '+spotSize+'%)');
            // Left edge spots
            layers.push('radial-gradient(ellipse at 0% '+pct+'%, '+rgba(tLeft[si],.55)+' 0%, transparent '+spotSize+'%)');
            // Right edge spots
            layers.push('radial-gradient(ellipse at 100% '+pct+'%, '+rgba(tRight[si],.55)+' 0%, transparent '+spotSize+'%)');
          }

          // Base fill
          layers.push(rgb(tame(overall)));

          bg = layers.join(', ');
        }

        container.style.background = bg;
        container.style.transition = 'background 0.5s ease';
        if (src) _adaptiveBgCache.set(src, bg);
        if (_adaptiveBgCache.size > 60) _adaptiveBgCache.delete(_adaptiveBgCache.keys().next().value);
      } catch (e) {
        container.style.background = '#f7f7f7';
      }
    };

    if (img.complete && img.naturalWidth) apply();
    else img.addEventListener('load', apply, { once: true });
  }

  function formatMoney(cents) {
    // Handle Shopify Money objects (e.g. {amount: "4300.00", currency_code: "KES"})
    if (cents && typeof cents === 'object') {
      cents = Math.round(parseFloat(cents.amount || cents.value || 0) * 100);
    }
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    const config = window.ARTFIT || {};
    const currencyObj = config.currency || {};
    const symbol = currencyObj.symbol || 'KSh';
    return symbol + amount;
  }

  function enableSwipe(el, { onSwipeLeft, onSwipeRight, threshold = 50 }) {
    let startX = 0;
    let startY = 0;
    el.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    el.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > threshold) {
        if (dx < 0) onSwipeLeft?.();
        else onSwipeRight?.();
      }
    }, { passive: true });
  }

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
    first.focus();
  }

  function escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ==========================================================================
     2. CART API
     ========================================================================== */

  const CartAPI = {
    async getCart() {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Failed to fetch cart');
      return res.json();
    },

    async addItem(variantId, quantity = 1, properties = {}) {
      const body = { id: variantId, quantity };
      if (Object.keys(properties).length) body.properties = properties;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Failed to add item');
      }
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    async addItems(items) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('Failed to add items');
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    async changeItem(key, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity })
      });
      if (!res.ok) throw new Error('Failed to update item');
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    async removeItem(key) {
      return this.changeItem(key, 0);
    },

    async updateNote(note) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ note })
      });
      return res.json();
    }
  };

  /* ==========================================================================
     3. WISHLIST STATE
     ========================================================================== */

  const Wishlist = {
    _key: 'artfit_wishlist',

    // Returns array of {id, handle} objects
    getAll() {
      try {
        const raw = JSON.parse(localStorage.getItem(this._key)) || [];
        // Migrate legacy format (plain ID strings) to new {id, handle} format
        return raw.map(item => {
          if (typeof item === 'string' || typeof item === 'number') {
            return { id: String(item), handle: '' };
          }
          return { id: String(item.id), handle: item.handle || '' };
        });
      } catch {
        return [];
      }
    },

    // Returns flat array of ID strings (for quick checks)
    getAllIds() {
      return this.getAll().map(item => item.id);
    },

    _save(list) {
      localStorage.setItem(this._key, JSON.stringify(list));
      this._syncToServer(list);
    },

    toggle(productId, handle) {
      const list = this.getAll();
      const id = String(productId);
      const idx = list.findIndex(item => item.id === id);
      let added = false;
      if (idx > -1) {
        list.splice(idx, 1);
      } else {
        list.push({ id, handle: handle || '' });
        added = true;
      }
      this._save(list);
      this.updateUI();
      return added;
    },

    isWishlisted(productId) {
      return this.getAllIds().includes(String(productId));
    },

    count() {
      return this.getAll().length;
    },

    updateUI() {
      $$('[data-wishlist-toggle]').forEach((btn) => {
        const id = btn.dataset.productId;
        if (!id) return;
        const wishlisted = this.isWishlisted(id);
        btn.classList.toggle('is-wishlisted', wishlisted);
        btn.setAttribute('aria-pressed', wishlisted);
        const icon = $('svg', btn) || $('i', btn);
        if (icon) {
          icon.classList.toggle('filled', wishlisted);
        }
      });
      $$('[data-wishlist-count]').forEach((el) => {
        const c = this.count();
        el.textContent = c;
        el.style.display = c > 0 ? '' : 'none';
      });
    },

    // Sync: load from server, merge with local, save back
    async syncWithAccount() {
      const config = window.ARTFIT || {};
      const customer = config.customer || {};
      const apiUrl = config.wishlistApiUrl;
      const apiKey = config.wishlistApiKey;

      if (!customer.loggedIn || !customer.id || !apiUrl || !apiKey) return;

      try {
        // Load server wishlist
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
          body: JSON.stringify({ action: 'load', customer_id: customer.id })
        });
        if (!res.ok) return;

        const data = await res.json();
        const serverList = Array.isArray(data.wishlist) ? data.wishlist : [];
        const localList = this.getAll();

        // Merge: union of both lists (local wins for duplicates)
        const localIds = new Set(localList.map(item => item.id));
        let merged = false;

        serverList.forEach(item => {
          const normalized = typeof item === 'string' || typeof item === 'number'
            ? { id: String(item), handle: '' }
            : { id: String(item.id), handle: item.handle || '' };

          if (!localIds.has(normalized.id)) {
            localList.push(normalized);
            localIds.add(normalized.id);
            merged = true;
          }
        });

        if (merged) {
          localStorage.setItem(this._key, JSON.stringify(localList));
          this.updateUI();
        }

        // Save merged list back to server (if local had items server didn't)
        if (merged || localList.length !== serverList.length) {
          this._syncToServer(localList);
        }
      } catch (e) {
        // Silent fail — API may not be configured yet
      }
    },

    // Debounced save to server
    _syncTimer: null,
    _syncToServer(list) {
      const config = window.ARTFIT || {};
      const customer = config.customer || {};
      const apiUrl = config.wishlistApiUrl;
      const apiKey = config.wishlistApiKey;

      if (!customer.loggedIn || !customer.id || !apiUrl || !apiKey) return;

      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(() => {
        fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
          body: JSON.stringify({
            action: 'save',
            customer_id: customer.id,
            wishlist: list || this.getAll()
          })
        }).catch(() => {});
      }, 1000);
    }
  };

  /* ==========================================================================
     4. RECENTLY VIEWED
     ========================================================================== */

  const RecentlyViewed = {
    _key: 'artfit_recently_viewed_v2',
    _max: 10,

    getAll() {
      try {
        return JSON.parse(localStorage.getItem(this._key)) || [];
      } catch {
        return [];
      }
    },

    add(handle) {
      if (!handle) return;
      const list = this.getAll();
      const val = String(handle);
      const idx = list.indexOf(val);
      if (idx > -1) list.splice(idx, 1);
      list.unshift(val);
      if (list.length > this._max) list.pop();
      localStorage.setItem(this._key, JSON.stringify(list));
    },

    getExcluding(currentHandle) {
      return this.getAll().filter((h) => h !== String(currentHandle));
    }
  };

  /* ==========================================================================
     5. CART DRAWER
     ========================================================================== */

  function initCartDrawer() {
    const drawer = $('#cart-drawer');
    if (!drawer) return;

    const overlay = $('#cart-drawer-overlay') || $('.cart-drawer__overlay', drawer);
    const closeBtn = $('[data-cart-drawer-close]', drawer);
    const body = $('[data-cart-drawer-body]', drawer) || $('.cart-drawer__body', drawer);
    function open() {
      drawer.classList.add('is-open');
      document.body.classList.add('drawer-open');
      if (overlay) overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      refresh();
      trapFocus(drawer);
    }

    function close() {
      drawer.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      if (overlay) overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }

    const emptyCartHTML = `
      <div class="cart-drawer__empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="opacity:0.3"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
        <p class="cart-drawer__empty-title">Your cart is empty</p>
        <p class="cart-drawer__empty-text">Looks like you haven't added anything yet.</p>
        <a href="/collections/all" class="btn btn--primary btn--sm" style="margin-top:0.5rem">Start Shopping</a>
      </div>`;

    function renderCartItem(item) {
      const image = item.image
        ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_180x$1')}" alt="${escapeHTML(item.product_title)}" width="80" height="100" loading="lazy">`
        : '';

      const props = item.properties || {};
      const isGiftCard = !!props['Gift Card Value'];

      const variantTitle = (!isGiftCard && item.variant_title && item.variant_title !== 'Default Title')
        ? `<p class="cart-item__variant">${escapeHTML(item.variant_title)}</p>`
        : '';

      const preorderBadge = (props._preorder)
        ? '<span class="cart-item__preorder-badge">Pre-Order</span>'
        : '';

      const originalPrice = item.original_line_price > item.final_line_price
        ? `<s class="cart-item__price-compare">${formatMoney(item.original_line_price)}</s>`
        : '';

      // Gift card: show recipient info and value instead of qty controls
      let giftCardMeta = '';
      if (isGiftCard) {
        const recipientName = props['Recipient Name'] || '';
        const gcValue = props['Gift Card Value'] || '';
        giftCardMeta = `<p class="cart-item__variant">${escapeHTML(gcValue)}</p>`;
        if (recipientName) {
          giftCardMeta += `<p class="cart-item__variant">To: ${escapeHTML(recipientName)}</p>`;
        }
      }

      // Gift cards: hide qty controls, show price as the gift card value
      const qtyControls = isGiftCard
        ? `<span class="cart-item__gc-label">Gift Card</span>`
        : `<div class="cart-item__quantity">
                <button type="button" class="cart-item__qty-btn" data-qty-change="-1" data-line-key="${item.key}" aria-label="Decrease quantity">
                  <svg width="12" height="2" viewBox="0 0 12 2"><path d="M0 1h12" stroke="currentColor" stroke-width="1.5"/></svg>
                </button>
                <span class="cart-item__qty-value">${item.quantity}</span>
                <button type="button" class="cart-item__qty-btn" data-qty-change="1" data-line-key="${item.key}" aria-label="Increase quantity">
                  <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1.5"/></svg>
                </button>
              </div>`;

      return `
        <div class="cart-item" data-cart-item data-line-key="${item.key}">
          <div class="cart-item__image">
            <a href="${item.url}">${image}</a>
          </div>
          <div class="cart-item__details">
            <div class="cart-item__header">
              <div>
                <a href="${item.url}" class="cart-item__title">${escapeHTML(item.product_title)}</a>
                ${isGiftCard ? giftCardMeta : variantTitle}
                ${preorderBadge}
              </div>
              <button type="button" class="cart-item__remove" data-remove-item data-line-key="${item.key}" aria-label="Remove ${escapeHTML(item.product_title)}">
                <svg width="16" height="16" viewBox="0 0 16 16"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5"/></svg>
              </button>
            </div>
            <div class="cart-item__footer">
              ${qtyControls}
              <div class="cart-item__price">
                <span>${formatMoney(item.final_line_price)}</span>
                ${originalPrice}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    async function refresh() {
      if (!body) return;
      try {
        const cart = await CartAPI.getCart();
        updateBadges(cart.item_count);

        const suggestions = $('[data-cart-suggestions]', drawer);
        if (cart.item_count === 0) {
          body.innerHTML = emptyCartHTML;
          const footer = $('[data-cart-drawer-footer]', drawer) || $('.cart-drawer__footer', drawer);
          if (footer) footer.style.display = 'none';
          if (suggestions) suggestions.style.display = '';
          return;
        }

        const itemsHTML = cart.items.map(renderCartItem).join('');
        body.innerHTML = itemsHTML;
        if (suggestions) suggestions.style.display = 'none';

        const footer = $('[data-cart-drawer-footer]', drawer) || $('.cart-drawer__footer', drawer);
        if (footer) {
          footer.style.display = '';
          const subtotal = $('[data-cart-subtotal]', footer);
          if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
        }
      } catch (err) {
        console.error('Cart refresh error:', err);
        body.innerHTML = '<p class="cart-drawer__error">Unable to load cart. Please try again.</p>';
      }
    }

    function updateBadges(count) {
      $$('[data-cart-count]').forEach((badge) => {
        badge.textContent = count;
        badge.style.display = count > 0 ? '' : 'none';
      });
    }

    // Quantity change — optimistic UI
    delegate(drawer, '[data-qty-change]', 'click', async (e, btn) => {
      const key = btn.dataset.lineKey;
      const change = parseInt(btn.dataset.qtyChange, 10);
      const itemEl = btn.closest('[data-cart-item]');
      const qtyEl = $('.cart-item__qty-value', itemEl);
      const current = parseInt(qtyEl.textContent, 10);
      const newQty = Math.max(0, current + change);

      // Optimistic: update UI immediately
      if (newQty === 0) {
        itemEl.classList.add('is-removing');
        setTimeout(() => itemEl.remove(), 300);
        // Update badge count optimistically
        const currentCount = parseInt($$('[data-cart-count]')[0]?.textContent || '0', 10);
        updateBadges(Math.max(0, currentCount - current));
        // Check if cart is now empty
        const remainingItems = $$('[data-cart-item]', body).filter(el => el !== itemEl);
        if (remainingItems.length === 0) {
          setTimeout(() => {
            body.innerHTML = emptyCartHTML;
            const footer = $('[data-cart-drawer-footer]', drawer) || $('.cart-drawer__footer', drawer);
            if (footer) footer.style.display = 'none';
            const sug = $('[data-cart-suggestions]', drawer);
            if (sug) sug.style.display = '';
          }, 350);
        }
      } else {
        qtyEl.textContent = newQty;
      }

      // Sync with server in background, then refresh to get accurate totals
      try {
        if (newQty === 0) {
          await CartAPI.removeItem(key);
        } else {
          await CartAPI.changeItem(key, newQty);
        }
        await refresh();
      } catch (err) {
        console.error('Qty change error:', err);
        await refresh(); // Revert to server state on error
      }
    });

    // Remove item — optimistic UI
    delegate(drawer, '[data-remove-item]', 'click', async (e, btn) => {
      const key = btn.dataset.lineKey;
      const itemEl = btn.closest('[data-cart-item]');

      // Optimistic: animate out immediately
      itemEl.classList.add('is-removing');
      setTimeout(() => itemEl.remove(), 300);

      const currentCount = parseInt($$('[data-cart-count]')[0]?.textContent || '0', 10);
      updateBadges(Math.max(0, currentCount - 1));

      const remainingItems = $$('[data-cart-item]', body).filter(el => el !== itemEl);
      if (remainingItems.length === 0) {
        setTimeout(() => {
          body.innerHTML = emptyCartHTML;
          const footer = $('[data-cart-drawer-footer]', drawer) || $('.cart-drawer__footer', drawer);
          if (footer) footer.style.display = 'none';
          const sug = $('[data-cart-suggestions]', drawer);
          if (sug) sug.style.display = '';
        }, 350);
      }

      // Sync with server, then refresh for accurate totals
      try {
        await CartAPI.removeItem(key);
        await refresh();
      } catch (err) {
        console.error('Remove error:', err);
        await refresh();
      }
    });

    // Open triggers
    delegate(document, '[data-cart-drawer-open]', 'click', (e) => {
      e.preventDefault();
      open();
    });

    // Close triggers
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Open drawer after add to cart (refresh is called inside open())
    document.addEventListener('cart:item-added', () => {
      open();
    });

    // Expose
    window.ARTFIT = window.ARTFIT || {};
    window.ARTFIT.cartDrawer = { open, close, refresh };
  }

  /* ==========================================================================
     6. HEADER
     ========================================================================== */

  function initHeader() {
    const header = $('#site-header') || $('header');
    if (!header) return;

    // Scroll behavior
    let lastScroll = 0;
    const scrollThreshold = 50;

    const handleScroll = throttle(() => {
      const scrollY = window.scrollY;
      header.classList.toggle('is-scrolled', scrollY > scrollThreshold);

      if (scrollY > lastScroll && scrollY > 200) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      lastScroll = scrollY;
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Mobile menu
    const mobileToggle = $('[data-mobile-menu-toggle]', header);
    const mobileMenu = $('#mobile-menu');
    const mobileOverlay = $('#mobile-menu-overlay') || $('.mobile-menu__overlay');

    function openMobileMenu() {
      if (!mobileMenu) return;
      mobileMenu.classList.add('is-open');
      document.body.classList.add('menu-open');
      if (mobileOverlay) mobileOverlay.classList.add('is-active');
      mobileMenu.setAttribute('aria-hidden', 'false');
      if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'true');
      trapFocus(mobileMenu);
    }

    function closeMobileMenu() {
      if (!mobileMenu) return;
      mobileMenu.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      if (mobileOverlay) mobileOverlay.classList.remove('is-active');
      mobileMenu.setAttribute('aria-hidden', 'true');
      if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'false');
      // Reset any open submenus
      $$('[data-has-submenu].is-open', mobileMenu).forEach((item) => {
        item.classList.remove('is-open');
        const t = $('[data-submenu-toggle]', item);
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    if (mobileToggle) {
      mobileToggle.addEventListener('click', () => {
        const isOpen = mobileMenu && mobileMenu.classList.contains('is-open');
        if (isOpen) closeMobileMenu();
        else openMobileMenu();
      });
    }

    // Bind all close triggers (overlay + close button)
    if (mobileMenu) {
      $$('[data-mobile-menu-close]', mobileMenu).forEach((el) => {
        el.addEventListener('click', closeMobileMenu);
      });
    }

    // Mobile submenu slide-in
    if (mobileMenu) {
      delegate(mobileMenu, '[data-submenu-toggle]', 'click', (e, trigger) => {
        e.preventDefault();
        const parent = trigger.closest('[data-has-submenu]');
        if (!parent) return;

        // Close any other open submenu first
        $$('[data-has-submenu].is-open', mobileMenu).forEach((item) => {
          if (item !== parent) {
            item.classList.remove('is-open');
            const t = $('[data-submenu-toggle]', item);
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });

        parent.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      });

      // Back button closes submenu
      delegate(mobileMenu, '[data-submenu-back]', 'click', (e, btn) => {
        e.preventDefault();
        const parent = btn.closest('[data-has-submenu]');
        if (!parent) return;
        parent.classList.remove('is-open');
        const t = $('[data-submenu-toggle]', parent);
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    // Cart toggle
    delegate(document, '[data-cart-toggle]', 'click', (e) => {
      e.preventDefault();
      if (window.ARTFIT && window.ARTFIT.cartDrawer) {
        window.ARTFIT.cartDrawer.open();
      }
    });

    // Search toggle
    delegate(document, '[data-search-toggle]', 'click', (e) => {
      e.preventDefault();
      if (window.ARTFIT && window.ARTFIT.searchDrawer) {
        window.ARTFIT.searchDrawer.open();
      }
    });

    window.ARTFIT = window.ARTFIT || {};
    window.ARTFIT.mobileMenu = { open: openMobileMenu, close: closeMobileMenu };
  }

  /* ==========================================================================
     7. MEGA MENU
     ========================================================================== */

  function initMegaMenu() {
    const navItems = $$('[data-mega-menu-trigger]');
    if (!navItems.length) return;

    let activeMenu = null;
    let closeTimer = null;

    function openMenu(trigger) {
      const menuId = trigger.dataset.megaMenuTrigger;
      const menu = $(`[data-mega-menu-id="${menuId}"]`);
      if (!menu) return;

      if (activeMenu && activeMenu !== menu) {
        closeMenu(activeMenu);
      }

      clearTimeout(closeTimer);
      menu.classList.add('is-active');
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
      activeMenu = menu;
      menu._trigger = trigger;
    }

    function closeMenu(menu) {
      if (!menu) return;
      menu.classList.remove('is-active');
      if (menu._trigger) {
        menu._trigger.classList.remove('is-active');
        menu._trigger.setAttribute('aria-expanded', 'false');
      }
      if (activeMenu === menu) activeMenu = null;
    }

    function scheduleClose(menu) {
      closeTimer = setTimeout(() => closeMenu(menu), 400);
    }

    navItems.forEach((trigger) => {
      const navItem = trigger.closest('.header__nav-item') || trigger;
      navItem.addEventListener('mouseenter', () => openMenu(trigger));
      navItem.addEventListener('mouseleave', () => {
        if (activeMenu) scheduleClose(activeMenu);
      });

      // Keyboard
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          openMenu(trigger);
          const menu = activeMenu;
          if (menu) {
            const firstLink = $('a', menu);
            if (firstLink) firstLink.focus();
          }
        }
        if (e.key === 'Escape') {
          if (activeMenu) closeMenu(activeMenu);
          trigger.focus();
        }
      });
    });

    $$('[data-mega-menu-id]').forEach((menu) => {
      menu.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      menu.addEventListener('mouseleave', () => scheduleClose(menu));
      menu.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const trigger = menu._trigger;
          closeMenu(menu);
          if (trigger) trigger.focus();
        }
      });
    });
  }

  /* ==========================================================================
     8. HERO SLIDER
     ========================================================================== */

  function initHero() {
    const hero = $('[data-hero-slider]');
    if (!hero) return;

    const viewport = $('.hero__viewport', hero);
    const slides = $$('[data-hero-slide]', hero);
    if (slides.length <= 1) {
      // Single slide: just mark it active
      if (slides.length === 1) slides[0].classList.add('is-active');
      return;
    }

    const dotsContainer = $('[data-hero-dots]', hero);
    const prevBtn = $('[data-hero-prev]', hero);
    const nextBtn = $('[data-hero-next]', hero);
    const total = slides.length;
    let current = 0;
    let autoTimer = null;
    const autoDelay = 8000;
    let isPaused = false;

    // Set CSS custom properties for 3-slide peek carousel positions
    function setPositions() {
      if (!viewport) return;
      const vw = viewport.offsetWidth;
      const slideW = slides[0].offsetWidth;
      const center = (vw - slideW) / 2;
      const gap = 8; // small gap between slides
      const prev = center - slideW - gap;
      const next = center + slideW + gap;

      viewport.style.setProperty('--pos-prev', prev + 'px');
      viewport.style.setProperty('--pos-center', center + 'px');
      viewport.style.setProperty('--pos-next', next + 'px');
    }

    setPositions();
    window.addEventListener('resize', debounce(setPositions, 200));

    function updateSlides() {
      slides.forEach((slide, i) => {
        slide.classList.remove('is-active', 'is-prev', 'is-next');
        slide.setAttribute('aria-hidden', 'true');
      });

      const prevIndex = (current - 1 + total) % total;
      const nextIndex = (current + 1) % total;

      slides[current].classList.add('is-active');
      slides[current].setAttribute('aria-hidden', 'false');
      slides[prevIndex].classList.add('is-prev');
      slides[nextIndex].classList.add('is-next');

      if (dotsContainer) {
        $$('[data-hero-dot]', dotsContainer).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === current);
          dot.setAttribute('aria-selected', i === current);
        });
      }
    }

    function goTo(index) {
      current = ((index % total) + total) % total;
      updateSlides();
      resetAuto();
    }

    function goNext() {
      goTo(current + 1);
    }

    function goPrev() {
      goTo(current - 1);
    }

    function startAuto() {
      stopAuto();
      if (!isPaused) {
        autoTimer = setInterval(goNext, autoDelay);
      }
    }

    function stopAuto() {
      clearInterval(autoTimer);
    }

    function resetAuto() {
      stopAuto();
      startAuto();
    }

    // Build dots
    if (dotsContainer) {
      dotsContainer.innerHTML = slides.map((_, i) =>
        `<button type="button" data-hero-dot class="hero__dot${i === 0 ? ' is-active' : ''}" aria-label="Go to slide ${i + 1}" aria-selected="${i === 0}"></button>`
      ).join('');

      delegate(dotsContainer, '[data-hero-dot]', 'click', (e, dot) => {
        const idx = $$('[data-hero-dot]', dotsContainer).indexOf(dot);
        goTo(idx);
      });
    }

    if (prevBtn) prevBtn.addEventListener('click', goPrev);
    if (nextBtn) nextBtn.addEventListener('click', goNext);

    // Pause on hover
    hero.addEventListener('mouseenter', () => { isPaused = true; stopAuto(); });
    hero.addEventListener('mouseleave', () => { isPaused = false; startAuto(); });

    // Swipe
    enableSwipe(hero, { onSwipeLeft: goNext, onSwipeRight: goPrev });

    // Keyboard
    hero.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    });

    updateSlides();
    startAuto();
  }

  /* ==========================================================================
     9. PRODUCT SLIDERS
     ========================================================================== */

  function initProductSliders() {
    $$('[data-product-slider]').forEach((slider) => {
      const track = $('[data-slider-track]', slider) || $('.product-slider__track', slider);
      if (!track) return;

      const prevBtn = $('[data-slider-prev]', slider);
      const nextBtn = $('[data-slider-next]', slider);
      const scrollAmount = () => track.clientWidth * 0.8;

      function updateArrows() {
        const atStart = track.scrollLeft <= 5;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
        if (prevBtn) prevBtn.disabled = atStart;
        if (nextBtn) nextBtn.disabled = atEnd;
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
        });
      }

      track.addEventListener('scroll', throttle(updateArrows, 100), { passive: true });
      updateArrows();

      // Recalc on resize
      window.addEventListener('resize', debounce(updateArrows, 200));
    });
  }

  /* ==========================================================================
     10. PRODUCT CARD INTERACTIONS
     ========================================================================== */

  function initProductCards(container = document) {
    // Quick add
    delegate(container, '[data-quick-add], [data-action="quick-add"]', 'click', async (e, btn) => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.dataset.variantId;
      const productHandle = btn.dataset.productHandle;
      const hasMultipleVariants = btn.dataset.multipleVariants === 'true';

      if (hasMultipleVariants && productHandle) {
        if (window.ARTFIT && window.ARTFIT.quickView) {
          window.ARTFIT.quickView.open(productHandle);
        }
        return;
      }

      var parsedVariantId = parseInt(variantId, 10);
      if (!parsedVariantId || isNaN(parsedVariantId)) return;

      var originalText = btn.textContent;
      const isPreorder = btn.dataset.preorder === 'true';
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.textContent = isPreorder ? 'Pre-Ordering...' : 'Adding...';
      try {
        const quickAddProps = isPreorder ? { '_preorder': 'true' } : {};
        await CartAPI.addItem(parsedVariantId, 1, quickAddProps);
        document.dispatchEvent(new CustomEvent('cart:item-added'));
        btn.textContent = isPreorder ? 'Pre-Ordered!' : 'Added!';
        btn.classList.add('is-added');
        setTimeout(function() {
          btn.classList.remove('is-added');
          btn.textContent = originalText;
        }, 1500);
      } catch (err) {
        console.error('Quick add error:', err);
        btn.textContent = originalText;
        btn.classList.add('is-error');
        setTimeout(function() { btn.classList.remove('is-error'); }, 2000);
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    });

    // Wishlist toggle
    delegate(container, '[data-wishlist-toggle]', 'click', (e, btn) => {
      e.preventDefault();
      e.stopPropagation();
      const productId = btn.dataset.productId;
      const productHandle = btn.dataset.productHandle || '';
      if (productId) Wishlist.toggle(productId, productHandle);
    });

    // Quick view button
    delegate(container, '[data-action="quick-view"]', 'click', async (e, btn) => {
      e.preventDefault();
      e.stopPropagation();
      const handle = btn.dataset.productHandle || btn.closest('[data-product-handle]')?.dataset.productHandle;
      if (handle && window.ARTFIT && window.ARTFIT.quickView) {
        window.ARTFIT.quickView.open(handle);
        return;
      }
      // Fallback: resolve handle from product ID via the product card link
      const card = btn.closest('.product-card');
      const link = card ? $('a[href*="/products/"]', card) : null;
      if (link) {
        const match = link.getAttribute('href').match(/\/products\/([^?#/]+)/);
        if (match && window.ARTFIT && window.ARTFIT.quickView) {
          window.ARTFIT.quickView.open(match[1]);
        }
      }
    });

    // Color swatch hover on card
    delegate(container, '[data-swatch-color]', 'mouseenter', (e, swatch) => {
      const card = swatch.closest('.product-card');
      if (!card) return;
      const imageUrl = swatch.dataset.image;
      if (!imageUrl) return;
      const img = $('.product-card__image--primary', card);
      if (img) {
        if (!img.dataset.originalSrc) img.dataset.originalSrc = img.src;
        img.src = imageUrl;
      }
      $$('[data-swatch-color]', card).forEach((s) => s.classList.remove('product-card__swatch--active'));
      swatch.classList.add('product-card__swatch--active');
    });

    delegate(container, '[data-swatch-color]', 'click', (e, swatch) => {
      e.preventDefault();
    });

    // Reset image on card mouse leave
    delegate(container, '.product-card', 'mouseleave', (e, card) => {
      const img = $('.product-card__image--primary', card);
      if (img && img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
      }
    });
  }

  /* ==========================================================================
     11. VARIANT SELECTOR (PDP)
     ========================================================================== */

  function initVariantSelector() {
    const container = $('[data-variant-selector]');
    if (!container) return;

    const jsonEl = $('#product-variants-json');
    if (!jsonEl) return;

    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch {
      console.error('Failed to parse variants JSON');
      return;
    }

    const productForm = container.closest('form') || $('form[data-product-form]');
    const variantInput = productForm ? $('input[name="id"]', productForm) : null;
    const pricingContainer = $('#pdp-pricing');
    const priceEl = $('[data-product-price]');
    const addToCartBtn = $('[data-add-to-cart]');
    const addToCartText = addToCartBtn ? $('[data-add-to-cart-text]', addToCartBtn) || addToCartBtn : null;

    let selectedOptions = {};

    // Initialize selected options from current UI state
    $$('[data-option-group]', container).forEach((group) => {
      const name = group.dataset.optionName;
      const activeBtn = $('[data-option-value].is-selected', group) || $('[data-option-value]', group);
      if (activeBtn) {
        selectedOptions[name] = activeBtn.dataset.optionValue;
      }
    });

    function findVariant() {
      return variants.find((v) => {
        return v.options.every((opt, i) => {
          const optionName = getOptionName(i);
          return selectedOptions[optionName] === opt;
        });
      });
    }

    function getOptionName(index) {
      const group = $$('[data-option-group]', container)[index];
      return group ? group.dataset.optionName : `option${index + 1}`;
    }

    function getOptionIndex(name) {
      const groups = $$('[data-option-group]', container);
      return groups.findIndex((g) => g.dataset.optionName === name);
    }

    function updateAvailability() {
      $$('[data-option-group]', container).forEach((group) => {
        const name = group.dataset.optionName;
        const groupIndex = getOptionIndex(name);

        $$('[data-option-value]', group).forEach((btn) => {
          const value = btn.dataset.optionValue;
          // Check if any variant with this option value is available
          const testOptions = { ...selectedOptions, [name]: value };
          const available = variants.some((v) => {
            const matches = v.options.every((opt, i) => {
              const optName = getOptionName(i);
              if (optName === name) return opt === value;
              // For other options, check if they match currently selected
              return testOptions[optName] === undefined || testOptions[optName] === opt;
            });
            return matches && v.available;
          });

          btn.classList.toggle('is-unavailable', !available);
          btn.disabled = false; // Allow selecting unavailable to show "Sold Out"
        });
      });
    }

    function updateUI(variant) {
      // Update price
      if (variant) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

        if (priceEl) priceEl.textContent = formatMoney(variant.price);

        if (pricingContainer) {
          // Remove existing compare & badge if present
          const oldCompare = $('[data-product-compare-price]', pricingContainer);
          const oldBadge = $('[data-product-save-badge]', pricingContainer);
          if (oldCompare) oldCompare.remove();
          if (oldBadge) oldBadge.remove();

          if (onSale) {
            const pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
            const compareEl = document.createElement('s');
            compareEl.className = 'pdp__price-compare';
            compareEl.setAttribute('data-product-compare-price', '');
            compareEl.textContent = formatMoney(variant.compare_at_price);
            pricingContainer.appendChild(compareEl);

            const badgeEl = document.createElement('span');
            badgeEl.className = 'pdp__price-badge badge badge--sale';
            badgeEl.setAttribute('data-product-save-badge', '');
            badgeEl.textContent = `-${pct}%`;
            pricingContainer.appendChild(badgeEl);
          }
        }

        // Update variant input
        if (variantInput) variantInput.value = variant.id;

        // Update ATC button
        if (addToCartBtn && addToCartText) {
          const isPreorder = addToCartBtn.dataset.preorder;
          if (variant.available) {
            addToCartBtn.disabled = false;
            addToCartText.textContent = isPreorder ? 'Pre-Order Now' : 'Add to Cart';
          } else {
            addToCartBtn.disabled = true;
            addToCartText.textContent = 'Sold Out';
          }
        }

        // Update URL without reload
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState(null, '', url.toString());

        // Dispatch event
        document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));

        // Update gallery if variant has featured image
        if (variant.featured_image && window.ARTFIT && window.ARTFIT.productGallery) {
          window.ARTFIT.productGallery.goToImage(variant.featured_image.position - 1);
        }
      } else {
        // No matching variant
        if (addToCartBtn && addToCartText) {
          addToCartBtn.disabled = true;
          addToCartText.textContent = 'Unavailable';
        }
      }
    }

    // Option click handler
    delegate(container, '[data-option-value]', 'click', (e, btn) => {
      e.preventDefault();
      const group = btn.closest('[data-option-group]');
      const name = group.dataset.optionName;
      const value = btn.dataset.optionValue;

      // Update selection UI
      $$('[data-option-value]', group).forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      selectedOptions[name] = value;

      // Update label
      const label = $('[data-option-selected]', group);
      if (label) label.textContent = value;

      updateAvailability();
      const variant = findVariant();
      updateUI(variant);
    });

    // Initial state
    updateAvailability();
    const initialVariant = findVariant();
    if (initialVariant) updateUI(initialVariant);
  }

  /* ==========================================================================
     12. PRODUCT GALLERY (PDP)
     ========================================================================== */

  function initProductGallery() {
    const gallery = $('[data-product-gallery]');
    if (!gallery) return;

    const mainImage = $('[data-gallery-main-image]', gallery) || $('[data-gallery-main] img', gallery);
    const mainContainer = $('[data-gallery-main]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const indicators = $$('[data-gallery-indicator]', gallery);
    const prevBtn = $('[data-gallery-prev]', gallery);
    const nextBtn = $('[data-gallery-next]', gallery);
    const images = [];
    let currentIndex = 0;

    // Collect images from data-images attribute on the gallery main container
    if (mainContainer && mainContainer.dataset.images) {
      try {
        JSON.parse(mainContainer.dataset.images).forEach((img) => {
          images.push({ src: img.src || img, srcset: img.srcset || '', alt: img.alt || '' });
        });
      } catch { /* ignore */ }
    }

    // Fallback: collect from thumb elements
    if (!images.length) {
      thumbs.forEach((thumb) => {
        images.push({
          src: thumb.dataset.imageSrc || thumb.href || $('img', thumb)?.src || '',
          srcset: '',
          alt: thumb.dataset.imageAlt || $('img', thumb)?.alt || ''
        });
      });
    }

    // Last resort: use current main image
    if (!images.length && mainImage) {
      images.push({ src: mainImage.src, srcset: mainImage.srcset || '', alt: mainImage.alt });
    }

    // Preload remaining gallery images after page is idle
    if (images.length > 1) {
      var preloadImages = function() {
        images.forEach(function(img, i) {
          if (i === 0) return; // skip current image
          var preload = new Image();
          preload.src = img.src;
        });
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(preloadImages);
      } else {
        setTimeout(preloadImages, 2000);
      }
    }

    function adaptiveArrowColors(img) {
      if (!img || !img.complete || !img.naturalWidth) return;
      try {
        var S = 32;
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = S; canvas.height = S;
        ctx.drawImage(img, 0, 0, S, S);
        var data = ctx.getImageData(0, 0, S, S).data;

        function edgeLum(startX, endX) {
          var r = 0, g = 0, b = 0, count = 0;
          for (var y = Math.floor(S * 0.25); y < Math.floor(S * 0.75); y++) {
            for (var x = startX; x < endX; x++) {
              var i = (y * S + x) * 4;
              r += data[i]; g += data[i+1]; b += data[i+2]; count++;
            }
          }
          if (!count) return 128;
          return (.299 * r + .587 * g + .114 * b) / count;
        }

        var leftLum = edgeLum(0, 4);
        var rightLum = edgeLum(S - 4, S);

        if (prevBtn) prevBtn.style.setProperty('--arrow-color', leftLum > 140 ? '#111' : '#fff');
        if (nextBtn) nextBtn.style.setProperty('--arrow-color', rightLum > 140 ? '#111' : '#fff');
      } catch (e) { /* cross-origin */ }
    }

    var hasNavigated = false;

    function goTo(index) {
      if (index < 0 || index >= images.length) return;
      var showLoading = hasNavigated;
      hasNavigated = true;
      currentIndex = index;

      if (mainImage) {
        // Only show loading state when navigating multi-image galleries
        if (showLoading && images.length > 1) {
          mainImage.style.opacity = '0.6';
          if (mainContainer) mainContainer.classList.add('is-loading');
        }

        mainImage.src = images[index].src;
        if (images[index].srcset) mainImage.srcset = images[index].srcset;
        mainImage.alt = images[index].alt;
        adaptiveImageBg(mainImage, mainContainer);

        function onImageReady() {
          mainImage.style.opacity = '';
          if (mainContainer) mainContainer.classList.remove('is-loading');
          adaptiveArrowColors(mainImage);
        }

        if (mainImage.complete && mainImage.naturalWidth) {
          onImageReady();
        } else {
          mainImage.addEventListener('load', function onLoad() {
            onImageReady();
            mainImage.removeEventListener('load', onLoad);
          });
          mainImage.addEventListener('error', function onErr() {
            mainImage.style.opacity = '';
            if (mainContainer) mainContainer.classList.remove('is-loading');
            mainImage.removeEventListener('error', onErr);
          });
        }
      }

      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      indicators.forEach((ind, i) => ind.classList.toggle('is-active', i === index));

      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === images.length - 1;
    }

    function goToImage(index) {
      goTo(Math.max(0, Math.min(index, images.length - 1)));
    }

    // Thumb clicks
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        goTo(i);
      });
    });

    // Indicator clicks
    indicators.forEach((ind, i) => {
      ind.addEventListener('click', () => goTo(i));
    });

    // Arrows
    if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIndex + 1));

    // Mobile swipe on main container
    if (mainContainer) {
      enableSwipe(mainContainer, {
        onSwipeLeft: () => goTo(currentIndex + 1),
        onSwipeRight: () => goTo(currentIndex - 1)
      });
    }

    // Adaptive background color from image edges
    if (mainImage && mainContainer) {
      adaptiveImageBg(mainImage, mainContainer);
      if (mainImage.complete) {
        adaptiveArrowColors(mainImage);
      } else {
        mainImage.addEventListener('load', function onInitLoad() {
          adaptiveArrowColors(mainImage);
          mainImage.removeEventListener('load', onInitLoad);
        });
      }
    }

    // Zoom on click
    if (mainContainer || mainImage) {
      (mainContainer || mainImage).addEventListener('click', (e) => {
        if (e.target.closest('button')) return;
        openZoom(currentIndex);
      });
    }

    function openZoom(index) {
      if (!images.length) return;
      const overlay = document.createElement('div');
      overlay.className = 'gallery-zoom-overlay';
      overlay.innerHTML = `
        <button type="button" class="gallery-zoom__close" aria-label="Close zoom">
          <svg width="24" height="24" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" stroke-width="2"/></svg>
        </button>
        <div class="gallery-zoom__image-wrap">
          <img src="${images[index].src}" alt="${escapeHTML(images[index].alt)}" class="gallery-zoom__image">
        </div>
        <button type="button" class="gallery-zoom__prev" aria-label="Previous image">
          <svg width="24" height="24" viewBox="0 0 24 24"><path d="M15 4l-8 8 8 8" stroke="currentColor" stroke-width="2" fill="none"/></svg>
        </button>
        <button type="button" class="gallery-zoom__next" aria-label="Next image">
          <svg width="24" height="24" viewBox="0 0 24 24"><path d="M9 4l8 8-8 8" stroke="currentColor" stroke-width="2" fill="none"/></svg>
        </button>
      `;
      document.body.appendChild(overlay);
      document.body.classList.add('zoom-open');

      let zoomIndex = index;
      const zoomImg = $('.gallery-zoom__image', overlay);
      const imageWrap = $('.gallery-zoom__image-wrap', overlay);

      function updateZoom(i) {
        zoomIndex = ((i % images.length) + images.length) % images.length;
        resetPinchZoom();
        zoomImg.src = images[zoomIndex].src;
        zoomImg.alt = images[zoomIndex].alt;
      }

      // Pinch-to-zoom
      var scale = 1, lastScale = 1, translateX = 0, translateY = 0;
      var pinchStartDist = 0, pinchStartScale = 1;
      var panStartX = 0, panStartY = 0, panStartTX = 0, panStartTY = 0;
      var isPinching = false, isPanning = false;

      function applyTransform() {
        zoomImg.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + scale + ')';
      }

      function resetPinchZoom() {
        scale = 1; lastScale = 1; translateX = 0; translateY = 0;
        zoomImg.style.transform = '';
      }

      function getPinchDist(touches) {
        var dx = touches[0].clientX - touches[1].clientX;
        var dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      imageWrap.addEventListener('touchstart', function(e) {
        if (e.touches.length === 2) {
          isPinching = true;
          pinchStartDist = getPinchDist(e.touches);
          pinchStartScale = scale;
          e.preventDefault();
        } else if (e.touches.length === 1 && scale > 1) {
          isPanning = true;
          panStartX = e.touches[0].clientX;
          panStartY = e.touches[0].clientY;
          panStartTX = translateX;
          panStartTY = translateY;
          e.preventDefault();
        }
      }, { passive: false });

      imageWrap.addEventListener('touchmove', function(e) {
        if (isPinching && e.touches.length === 2) {
          var dist = getPinchDist(e.touches);
          scale = Math.min(4, Math.max(1, pinchStartScale * (dist / pinchStartDist)));
          if (scale <= 1) { translateX = 0; translateY = 0; }
          applyTransform();
          e.preventDefault();
        } else if (isPanning && e.touches.length === 1 && scale > 1) {
          translateX = panStartTX + (e.touches[0].clientX - panStartX);
          translateY = panStartTY + (e.touches[0].clientY - panStartY);
          applyTransform();
          e.preventDefault();
        }
      }, { passive: false });

      imageWrap.addEventListener('touchend', function(e) {
        if (e.touches.length < 2) isPinching = false;
        if (e.touches.length === 0) isPanning = false;
        if (scale <= 1) resetPinchZoom();
        lastScale = scale;
      }, { passive: true });

      // Double-tap to zoom/reset
      var lastTap = 0;
      imageWrap.addEventListener('touchend', function(e) {
        if (e.touches.length > 0) return;
        var now = Date.now();
        if (now - lastTap < 300) {
          if (scale > 1) {
            resetPinchZoom();
          } else {
            scale = 2.5;
            applyTransform();
          }
          lastScale = scale;
          e.preventDefault();
        }
        lastTap = now;
      }, { passive: false });

      $('.gallery-zoom__close', overlay).addEventListener('click', closeZoom);
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeZoom();
      });
      $('.gallery-zoom__prev', overlay).addEventListener('click', () => updateZoom(zoomIndex - 1));
      $('.gallery-zoom__next', overlay).addEventListener('click', () => updateZoom(zoomIndex + 1));

      enableSwipe(overlay, {
        onSwipeLeft: () => { if (scale <= 1) updateZoom(zoomIndex + 1); },
        onSwipeRight: () => { if (scale <= 1) updateZoom(zoomIndex - 1); }
      });

      document.addEventListener('keydown', handleZoomKey);

      function handleZoomKey(e) {
        if (e.key === 'Escape') closeZoom();
        if (e.key === 'ArrowLeft') updateZoom(zoomIndex - 1);
        if (e.key === 'ArrowRight') updateZoom(zoomIndex + 1);
      }

      function closeZoom() {
        document.body.removeChild(overlay);
        document.body.classList.remove('zoom-open');
        document.removeEventListener('keydown', handleZoomKey);
      }
    }

    goTo(0);

    // Expose
    window.ARTFIT = window.ARTFIT || {};
    window.ARTFIT.productGallery = { goToImage };
  }

  /* ==========================================================================
     13. QUICK VIEW MODAL
     ========================================================================== */

  function initQuickView() {
    const modal = $('#quick-view-modal');
    if (!modal) return;

    const overlay = $('.quick-view__overlay', modal) || modal;
    const content = $('[data-quick-view-content]', modal) || $('.quick-view__content', modal);
    let queue = [];
    let queueIndex = 0;
    let isProcessingQueue = false;

    async function open(handle) {
      if (!handle) return;
      modal.classList.add('is-open', 'is-loading');
      document.body.classList.add('modal-open');
      modal.setAttribute('aria-hidden', 'false');

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error('Product not found');
        const product = await res.json();
        renderProduct(product);
        modal.classList.remove('is-loading');
        // Adaptive bg for quick view main image
        const qvMainImg = $('[data-qv-main-image]', modal);
        if (qvMainImg) adaptiveImageBg(qvMainImg, qvMainImg.closest('.quick-view__gallery-main'));
        trapFocus(modal);
      } catch (err) {
        console.error('Quick view error:', err);
        if (content) {
          content.innerHTML = '<p class="quick-view__error">Unable to load product.</p>';
        }
        modal.classList.remove('is-loading');
      }
    }

    function close() {
      modal.classList.remove('is-open', 'is-loading');
      document.body.classList.remove('modal-open');
      modal.setAttribute('aria-hidden', 'true');
      queue = [];
      queueIndex = 0;
      isProcessingQueue = false;
    }

    function renderProduct(product) {
      if (!content) return;

      const variant = product.variants[0];
      const hasMultipleVariants = product.variants.length > 1;
      const images = product.images || [];
      const mainImg = images[0] || '';

      // Build options HTML
      let optionsHTML = '';
      if (hasMultipleVariants && product.options) {
        product.options.forEach((option, optIdx) => {
          const optName = typeof option === 'string' ? option : option.name;
          const values = option.values || [...new Set(product.variants.map((v) => v.options[optIdx]))];
          const isColor = /color|colour/i.test(optName);
          optionsHTML += `
            <div class="quick-view__option" data-qv-option-group data-option-name="${escapeHTML(optName)}" data-option-index="${optIdx}">
              <label class="quick-view__option-label">${escapeHTML(optName)}: <span data-qv-option-selected></span></label>
              <div class="quick-view__option-values ${isColor ? 'quick-view__option-values--color' : ''}">
                ${values.map((val, i) => {
                  const colorClass = isColor ? `style="background-color:${val.toLowerCase()}"` : '';
                  return `<button type="button" class="quick-view__option-btn ${i === 0 ? 'is-selected' : ''}" data-qv-option-value="${escapeHTML(val)}" ${colorClass}>${escapeHTML(val)}</button>`;
                }).join('')}
              </div>
            </div>
          `;
        });
      }

      // Gallery HTML — arrows + bar indicators (same as PDP)
      const barWidth = images.length > 1 ? (100 / images.length).toFixed(4) : 100;
      const galleryHTML = `<div class="quick-view__gallery">
            <div class="quick-view__gallery-main" data-qv-gallery-images='${JSON.stringify(images)}'>
              <img src="${mainImg}" alt="${escapeHTML(product.title)}" data-qv-main-image>
              ${images.length > 1 ? `
                <button class="product-gallery__arrow product-gallery__arrow--prev" type="button" aria-label="Previous image" data-qv-prev>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
                </button>
                <button class="product-gallery__arrow product-gallery__arrow--next" type="button" aria-label="Next image" data-qv-next>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </button>
                <div class="product-gallery__indicators">
                  ${images.map((_, i) =>
                    `<button class="product-gallery__bar${i === 0 ? ' is-active' : ''}" type="button" aria-label="Go to image ${i + 1}" data-qv-indicator="${i}" style="width:${barWidth}%"></button>`
                  ).join('')}
                </div>
              ` : ''}
            </div>
          </div>`;

      const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<s class="quick-view__compare-price">${formatMoney(variant.compare_at_price)}</s>`
        : '';

      content.innerHTML = `
        <div class="quick-view__inner">
          ${galleryHTML}
          <div class="quick-view__info">
            <h3 class="quick-view__title">${escapeHTML(product.title)}</h3>
            <div class="quick-view__price">
              ${comparePrice}
              <span data-qv-price>${formatMoney(variant.price)}</span>
            </div>
            ${optionsHTML}
            <button type="button" class="btn btn--primary quick-view__atc" data-qv-add-to-cart data-variant-id="${variant.id}" ${!variant.available ? 'disabled' : ''}>
              <span data-qv-atc-text>${variant.available ? 'Add to Cart' : 'Sold Out'}</span>
            </button>
            <a href="${product.url}" class="quick-view__full-link">View Full Details</a>
          </div>
        </div>
        ${isProcessingQueue ? renderQueueProgress() : ''}
      `;

      // Store variants for selection
      content._variants = product.variants;
      content._product = product;
      content._selectedOptions = {};
      if (product.options) {
        product.options.forEach((opt, i) => {
          const optName = typeof opt === 'string' ? opt : opt.name;
          content._selectedOptions[optName] = product.variants[0].options[i];
        });
      }
    }

    function renderQueueProgress() {
      return `
        <div class="quick-view__queue-progress">
          <div class="queue-progress__bar">
            <div class="queue-progress__fill" style="width:${((queueIndex + 1) / queue.length) * 100}%"></div>
          </div>
          <p class="queue-progress__text">Adding item ${queueIndex + 1} of ${queue.length}</p>
        </div>
      `;
    }

    // Gallery carousel (arrows + bar indicators)
    let qvIndex = 0;
    function qvGoTo(idx) {
      const galleryMain = $('[data-qv-gallery-images]', modal);
      if (!galleryMain) return;
      let imgs;
      try { imgs = JSON.parse(galleryMain.dataset.qvGalleryImages); } catch(e) { return; }
      if (!imgs || !imgs.length) return;
      idx = ((idx % imgs.length) + imgs.length) % imgs.length;
      qvIndex = idx;
      const mainImg = $('[data-qv-main-image]', modal);
      if (mainImg) {
        mainImg.src = imgs[idx];
        adaptiveImageBg(mainImg, galleryMain);
      }
      $$('[data-qv-indicator]', modal).forEach((bar) => {
        bar.classList.toggle('is-active', parseInt(bar.dataset.qvIndicator) === idx);
      });
    }
    delegate(modal, '[data-qv-prev]', 'click', () => qvGoTo(qvIndex - 1));
    delegate(modal, '[data-qv-next]', 'click', () => qvGoTo(qvIndex + 1));
    delegate(modal, '[data-qv-indicator]', 'click', (e, bar) => qvGoTo(parseInt(bar.dataset.qvIndicator)));

    // Option selection
    delegate(modal, '[data-qv-option-value]', 'click', (e, btn) => {
      const group = btn.closest('[data-qv-option-group]');
      const optName = group.dataset.optionName;
      const optValue = btn.dataset.qvOptionValue;

      $$('[data-qv-option-value]', group).forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      const selLabel = $('[data-qv-option-selected]', group);
      if (selLabel) selLabel.textContent = optValue;

      if (content._selectedOptions) {
        content._selectedOptions[optName] = optValue;
      }

      // Find matching variant
      const variants = content._variants;
      const product = content._product;
      if (variants && product) {
        const match = variants.find((v) =>
          v.options.every((opt, i) => {
            const optName = typeof product.options[i] === 'string' ? product.options[i] : product.options[i].name;
            return content._selectedOptions[optName] === opt;
          })
        );
        if (match) {
          const priceEl = $('[data-qv-price]', modal);
          if (priceEl) priceEl.textContent = formatMoney(match.price);

          const atcBtn = $('[data-qv-add-to-cart]', modal);
          const atcText = $('[data-qv-atc-text]', modal);
          if (atcBtn) {
            atcBtn.dataset.variantId = match.id;
            atcBtn.disabled = !match.available;
          }
          if (atcText) atcText.textContent = match.available ? 'Add to Cart' : 'Sold Out';

          // Update image if variant has featured image
          if (match.featured_image) {
            const mainImg = $('[data-qv-main-image]', modal);
            if (mainImg) mainImg.src = match.featured_image.src;
          }
        }
      }
    });

    // Add to cart
    delegate(modal, '[data-qv-add-to-cart]', 'click', async (e, btn) => {
      const variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;

      btn.classList.add('is-loading');
      btn.disabled = true;
      const atcText = $('[data-qv-atc-text]', modal);

      try {
        await CartAPI.addItem(variantId, 1);
        if (atcText) atcText.textContent = 'Added!';
        document.dispatchEvent(new CustomEvent('cart:item-added'));

        if (isProcessingQueue && queueIndex < queue.length - 1) {
          queueIndex++;
          setTimeout(() => open(queue[queueIndex]), 500);
        } else if (isProcessingQueue) {
          isProcessingQueue = false;
          setTimeout(close, 1000);
        } else {
          setTimeout(close, 1200);
        }
      } catch (err) {
        console.error('QV add error:', err);
        if (atcText) atcText.textContent = 'Error - Try Again';
        btn.disabled = false;
      } finally {
        btn.classList.remove('is-loading');
      }
    });

    // Queue mode for Complete the Look
    function openQueue(handles) {
      if (!handles.length) return;
      queue = handles;
      queueIndex = 0;
      isProcessingQueue = true;
      open(queue[0]);
    }

    // Close — bind all close triggers
    $$('[data-quick-view-close]', modal).forEach((el) => {
      el.addEventListener('click', close);
    });
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target === overlay) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    window.ARTFIT = window.ARTFIT || {};
    window.ARTFIT.quickView = { open, close, openQueue };
  }

  /* ==========================================================================
     14. SEARCH DRAWER
     ========================================================================== */

  function initSearchDrawer() {
    const drawer = $('#search-drawer');
    if (!drawer) return;

    const input = $('[data-search-input]', drawer) || $('input[type="search"]', drawer);
    const resultsContainer = $('[data-search-results]', drawer);
    const recentContainer = $('[data-recent-searches]', drawer);
    const popularContainer = $('[data-popular-searches]', drawer);
    const clearBtn = $('[data-search-clear]', drawer);
    const recentKey = 'artfit_recent_searches';

    function open() {
      drawer.classList.add('is-open');
      document.body.classList.add('search-open');
      if (overlay) overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
      showRecent();
    }

    function close() {
      drawer.classList.remove('is-open');
      document.body.classList.remove('search-open');
      if (overlay) overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }

    function getRecentSearches() {
      try {
        return JSON.parse(localStorage.getItem(recentKey)) || [];
      } catch {
        return [];
      }
    }

    function saveRecentSearch(query) {
      const recent = getRecentSearches();
      const q = query.trim();
      if (!q) return;
      const idx = recent.indexOf(q);
      if (idx > -1) recent.splice(idx, 1);
      recent.unshift(q);
      if (recent.length > 8) recent.pop();
      localStorage.setItem(recentKey, JSON.stringify(recent));
    }

    function clearRecentSearches() {
      localStorage.removeItem(recentKey);
      showRecent();
    }

    function showRecent() {
      if (!recentContainer) return;
      const recent = getRecentSearches();
      if (!recent.length) {
        recentContainer.style.display = 'none';
        return;
      }
      recentContainer.style.display = '';
      const list = $('[data-recent-list]', recentContainer) || recentContainer;
      list.innerHTML = recent.map((q) =>
        `<button type="button" class="search-drawer__recent-item" data-recent-query="${escapeHTML(q)}">${escapeHTML(q)}</button>`
      ).join('');
    }

    const performSearch = debounce(async (query) => {
      if (!query || query.length < 2) {
        if (resultsContainer) {
          resultsContainer.innerHTML = '';
          resultsContainer.style.display = 'none';
        }
        if (recentContainer) recentContainer.style.display = '';
        if (popularContainer) popularContainer.style.display = '';
        return;
      }

      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`
        );
        if (!res.ok) return;
        const data = await res.json();
        const products = data.resources?.results?.products || [];

        if (recentContainer) recentContainer.style.display = 'none';
        if (popularContainer) popularContainer.style.display = 'none';

        if (!resultsContainer) return;
        resultsContainer.style.display = '';

        if (!products.length) {
          resultsContainer.innerHTML = `<p class="search-drawer__no-results">No products found for "${escapeHTML(query)}"</p>`;
          return;
        }

        resultsContainer.innerHTML = products.map((p) => {
          const img = p.image
            ? `<img src="${p.image.replace(/(\.[^.]+)$/, '_120x$1')}" alt="${escapeHTML(p.title)}" loading="lazy" width="60" height="75">`
            : '<div class="search-result__placeholder"></div>';
          const price = p.price
            ? formatMoney(Math.round(parseFloat(p.price) * 100))
            : '';
          return `
            <a href="${p.url}" class="search-result" data-search-result>
              <div class="search-result__image">${img}</div>
              <div class="search-result__info">
                <p class="search-result__title">${escapeHTML(p.title)}</p>
                <p class="search-result__price">${price}</p>
              </div>
            </a>
          `;
        }).join('');
      } catch (err) {
        console.error('Search error:', err);
      }
    }, 300);

    if (input) {
      input.addEventListener('input', () => {
        performSearch(input.value.trim());
        if (clearBtn) clearBtn.style.display = input.value ? '' : 'none';
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const q = input.value.trim();
          if (q) {
            saveRecentSearch(q);
            window.location.href = `/search?q=${encodeURIComponent(q)}`;
          }
        }
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (input) {
          input.value = '';
          input.focus();
        }
        if (resultsContainer) {
          resultsContainer.innerHTML = '';
          resultsContainer.style.display = 'none';
        }
        clearBtn.style.display = 'none';
        showRecent();
        if (popularContainer) popularContainer.style.display = '';
      });
    }

    // Recent search click
    delegate(drawer, '[data-recent-query]', 'click', (e, btn) => {
      const q = btn.dataset.recentQuery;
      if (input) input.value = q;
      performSearch(q);
    });

    // Clear recent
    delegate(drawer, '[data-clear-recent]', 'click', () => {
      clearRecentSearches();
    });

    // Close — bind all elements with data-search-drawer-close
    $$('[data-search-drawer-close]', drawer).forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    window.ARTFIT = window.ARTFIT || {};
    window.ARTFIT.searchDrawer = { open, close };
  }

  /* ==========================================================================
     15. STICKY ADD TO CART (PDP)
     ========================================================================== */

  function initStickyAddToCart() {
    const stickyBar = $('[data-sticky-atc]');
    const mainAtcBtn = $('[data-add-to-cart]');
    if (!stickyBar || !mainAtcBtn) return;

    const stickyAtcBtn = $('[data-action="sticky-atc"]', stickyBar) || $('[data-sticky-atc-btn]', stickyBar);
    const stickyPrice = $('#sticky-atc-price') || $('[data-sticky-price]', stickyBar);
    const stickyVariant = $('#sticky-atc-variant');
    const stickyImage = $('#sticky-atc-image');

    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(mainAtcBtn);

    // Mirror variant changes
    document.addEventListener('variant:changed', (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;

      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (stickyVariant) stickyVariant.textContent = variant.title !== 'Default Title' ? variant.title : '';
      if (stickyAtcBtn) {
        stickyAtcBtn.disabled = !variant.available;
        const stickyPreorder = stickyAtcBtn.dataset.preorder;
        stickyAtcBtn.textContent = variant.available ? (stickyPreorder ? 'Pre-Order' : 'Add to Cart') : 'Sold Out';
        stickyAtcBtn.dataset.variantId = variant.id;
      }
      if (stickyImage && variant.featured_image) {
        stickyImage.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_80x$1');
      }
    });

    // Sticky ATC click
    if (stickyAtcBtn) {
      stickyAtcBtn.addEventListener('click', async () => {
        const variantId = parseInt(stickyAtcBtn.dataset.variantId, 10);
        if (!variantId) {
          // Scroll to variant selector with shake
          const selector = $('[data-variant-selector]');
          if (selector) {
            selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
            selector.classList.add('shake');
            setTimeout(() => selector.classList.remove('shake'), 600);
          }
          return;
        }

        stickyAtcBtn.classList.add('is-loading');
        stickyAtcBtn.disabled = true;
        try {
          const stickyProps = stickyAtcBtn.dataset.preorder ? { '_preorder': 'true' } : {};
          await CartAPI.addItem(variantId, 1, stickyProps);
          document.dispatchEvent(new CustomEvent('cart:item-added'));
        } catch (err) {
          console.error('Sticky ATC error:', err);
        } finally {
          stickyAtcBtn.classList.remove('is-loading');
          stickyAtcBtn.disabled = false;
        }
      });
    }
  }

  /* ==========================================================================
     16. COMPLETE THE LOOK (PDP)
     ========================================================================== */

  function initCompleteTheLook() {
    const section = $('[data-complete-the-look]');
    if (!section) return;

    const productId = section.dataset.productId;
    const container = $('[data-ctl-products]', section);
    const totalEl = $('[data-ctl-total]', section);
    const addAllBtn = $('[data-ctl-add-all]', section);
    if (!productId || !container) return;

    async function loadRecommendations() {
      // If products are already server-rendered from metafields, skip API fetch
      if (container.children.length > 0) {
        updateTotal();
        return;
      }
      try {
        const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=complementary`);
        if (!res.ok) return;
        const data = await res.json();
        const products = data.products || [];
        if (!products.length) {
          section.style.display = 'none';
          return;
        }
        renderProducts(products);
      } catch (err) {
        console.error('CTL error:', err);
        section.style.display = 'none';
      }
    }

    function updateTotal() {
      if (!totalEl) return;
      const priceEls = $$('.look-item__price', container);
      // Total is informational only — computed from server-rendered prices
      if (priceEls.length) {
        totalEl.textContent = priceEls.length + ' items';
      }
    }

    function renderProducts(products) {
      let total = 0;
      container.innerHTML = products.map((p) => {
        const price = p.price;
        total += price;
        const img = p.featured_image
          ? `<img src="${p.featured_image.replace(/(\.[^.]+)$/, '_300x$1')}" alt="${escapeHTML(p.title)}" loading="lazy">`
          : '';
        return `
          <div class="ctl__product" data-ctl-product data-product-handle="${p.handle}">
            <div class="ctl__product-image">
              <a href="${p.url}">${img}</a>
            </div>
            <div class="ctl__product-info">
              <a href="${p.url}" class="ctl__product-title">${escapeHTML(p.title)}</a>
              <p class="ctl__product-price">${formatMoney(price)}</p>
            </div>
            <button type="button" class="ctl__product-add" data-ctl-add data-product-handle="${p.handle}">
              <svg width="16" height="16" viewBox="0 0 16 16"><path d="M8 2v12M2 8h12" stroke="currentColor" stroke-width="2"/></svg>
              Add
            </button>
          </div>
        `;
      }).join('');

      if (totalEl) totalEl.textContent = formatMoney(total);
      container._handles = products.map((p) => p.handle);
    }

    // Add individual
    delegate(section, '[data-ctl-add]', 'click', (e, btn) => {
      const handle = btn.dataset.productHandle;
      if (handle && window.ARTFIT && window.ARTFIT.quickView) {
        window.ARTFIT.quickView.open(handle);
      }
    });

    // Add all
    if (addAllBtn) {
      addAllBtn.addEventListener('click', () => {
        const handles = container._handles;
        if (handles && handles.length && window.ARTFIT && window.ARTFIT.quickView) {
          window.ARTFIT.quickView.openQueue(handles);
        }
      });
    }

    loadRecommendations();
  }

  /* ==========================================================================
     17. PRODUCT RECOMMENDATIONS (PDP)
     ========================================================================== */

  function initProductRecommendations() {
    const section = $('[data-product-recommendations]');
    if (!section) return;

    const productId = section.dataset.productId;
    const limit = parseInt(section.dataset.limit, 10) || 8;
    const container = $('[data-recommendations-container]', section);
    if (!productId || !container) return;

    async function load() {
      try {
        const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`);
        if (!res.ok) return;
        const data = await res.json();
        const products = data.products || [];
        if (!products.length) {
          section.style.display = 'none';
          return;
        }
        render(products);
      } catch (err) {
        console.error('Recommendations error:', err);
      }
    }

    function render(products) {
      container.innerHTML = products.map((p) => {
        const img = p.featured_image
          ? `<img class="product-card__image" src="${p.featured_image.replace(/(\.[^.]+)$/, '_400x$1')}" alt="${escapeHTML(p.title)}" loading="lazy" data-product-image>`
          : '';
        const comparePrice = p.compare_at_price && p.compare_at_price > p.price
          ? `<s class="product-card__compare">${formatMoney(p.compare_at_price)}</s>`
          : '';
        const variant = p.variants && p.variants[0] ? p.variants[0] : null;
        const multipleVariants = p.variants && p.variants.length > 1;

        return `
          <div class="product-card" data-product-card>
            <div class="product-card__media">
              <a href="${p.url}" class="product-card__link">${img}</a>
              <button type="button" class="product-card__wishlist" data-wishlist-toggle data-product-id="${p.id}" aria-label="Add to wishlist" aria-pressed="${Wishlist.isWishlisted(p.id)}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 17.5s-7-4.5-7-9a4 4 0 017-2.5A4 4 0 0117 8.5c0 4.5-7 9-7 9z" stroke="currentColor" stroke-width="1.5"/></svg>
              </button>
              <button type="button" class="product-card__quick-add" data-quick-add data-variant-id="${variant ? variant.id : ''}" data-product-handle="${p.handle}" data-multiple-variants="${multipleVariants}">
                Quick Add
              </button>
            </div>
            <div class="product-card__info">
              <a href="${p.url}" class="product-card__title">${escapeHTML(p.title)}</a>
              <div class="product-card__price">
                ${comparePrice}
                <span>${formatMoney(p.price)}</span>
              </div>
            </div>
          </div>
        `;
      }).join('');

      initProductCards(container);
      Wishlist.updateUI();
    }

    load();
  }

  /* ==========================================================================
     18. ACCORDION
     ========================================================================== */

  function initAccordions() {
    $$('[data-accordion]').forEach((accordion) => {
      const singleOpen = accordion.hasAttribute('data-single-open');

      delegate(accordion, '[data-accordion-trigger]', 'click', (e, trigger) => {
        const item = trigger.closest('[data-accordion-item]');
        if (!item) return;
        const content = $('[data-accordion-content]', item);
        if (!content) return;

        const isOpen = item.classList.contains('is-open');

        if (singleOpen) {
          $$('[data-accordion-item].is-open', accordion).forEach((openItem) => {
            if (openItem !== item) {
              openItem.classList.remove('is-open');
              const openContent = $('[data-accordion-content]', openItem);
              if (openContent) openContent.style.maxHeight = '0px';
              const openTrigger = $('[data-accordion-trigger]', openItem);
              if (openTrigger) openTrigger.setAttribute('aria-expanded', 'false');
            }
          });
        }

        if (isOpen) {
          item.classList.remove('is-open');
          content.style.maxHeight = '0px';
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ==========================================================================
     19. CONTACT MODAL
     ========================================================================== */

  function initContactModal() {
    const overlay = $('#contact-modal-overlay');
    const modal = $('#contact-modal');
    if (!overlay || !modal) return;

    function open() {
      overlay.classList.add('is-open');
      document.body.classList.add('modal-open');
      modal.setAttribute('aria-hidden', 'false');
      trapFocus(modal);
    }

    function close() {
      overlay.classList.remove('is-open');
      document.body.classList.remove('modal-open');
      modal.setAttribute('aria-hidden', 'true');
    }

    delegate(document, '[data-contact-modal-open]', 'click', (e) => {
      e.preventDefault();
      open();
    });

    $$('[data-contact-modal-close]', overlay).forEach((btn) => btn.addEventListener('click', close));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    // WhatsApp link
    const config = window.ARTFIT || {};
    if (config.whatsappNumber) {
      $$('[data-whatsapp-link]', modal).forEach((link) => {
        link.href = `https://wa.me/${config.whatsappNumber.replace(/\D/g, '')}`;
      });
    }

    window.ARTFIT = window.ARTFIT || {};
    window.ARTFIT.contactModal = { open, close };
  }

  /* ==========================================================================
     20. MOBILE BOTTOM NAV
     ========================================================================== */

  function initMobileBottomNav() {
    const nav = $('[data-mobile-bottom-nav]');
    if (!nav) return;

    // Highlight active page
    const path = window.location.pathname;
    $$('[data-nav-page]', nav).forEach((item) => {
      const page = item.dataset.navPage;
      const isActive =
        (page === 'home' && path === '/') ||
        (page === 'collections' && path.startsWith('/collections')) ||
        (page === 'search' && path.startsWith('/search')) ||
        (page === 'cart' && path.startsWith('/cart'));
      item.classList.toggle('is-active', isActive);
    });

    // Search button
    delegate(nav, '[data-nav-search]', 'click', (e) => {
      e.preventDefault();
      if (window.ARTFIT && window.ARTFIT.searchDrawer) {
        window.ARTFIT.searchDrawer.open();
      }
    });

    // Cart button
    delegate(nav, '[data-nav-cart]', 'click', (e) => {
      e.preventDefault();
      if (window.ARTFIT && window.ARTFIT.cartDrawer) {
        window.ARTFIT.cartDrawer.open();
      }
    });

    // Contact button
    delegate(nav, '[data-nav-contact]', 'click', (e) => {
      e.preventDefault();
      if (window.ARTFIT && window.ARTFIT.contactModal) {
        window.ARTFIT.contactModal.open();
      }
    });
  }

  /* ==========================================================================
     21. SCROLL REVEAL
     ========================================================================== */

  function initScrollReveal() {
    const revealSelectors = '.reveal-heading, .reveal, .image-reveal';
    const elements = $$(revealSelectors);
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ==========================================================================
     22. THEME SWITCHER
     ========================================================================== */

  function initThemeSwitcher() {
    const storageKey = 'artfit_theme';

    // Apply saved theme
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      document.documentElement.setAttribute('data-theme', saved);
    }

    // Theme dots in header use data-theme="light" / data-theme="dark"
    delegate(document, '[data-theme]', 'click', (e, btn) => {
      const theme = btn.dataset.theme;
      if (!theme) return;
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem(storageKey, theme);

      // Update active state on all theme dots
      $$('[data-theme]').forEach((dot) => {
        dot.classList.toggle('is-active', dot.dataset.theme === theme);
      });
    });

    // Set initial active state
    const current = document.documentElement.getAttribute('data-theme') || 'light';
    $$('[data-theme]').forEach((dot) => {
      dot.classList.toggle('is-active', dot.dataset.theme === current);
    });
  }

  /* ==========================================================================
     23. RECENTLY VIEWED (PDP)
     ========================================================================== */

  function initRecentlyViewed() {
    const section = $('[data-recently-viewed]');
    const pdpSection = $('[data-product-handle]');
    const currentHandle = pdpSection ? pdpSection.dataset.productHandle : null;

    // Track current product by handle
    if (currentHandle) {
      RecentlyViewed.add(currentHandle);
    }

    if (!section) return;

    const container = $('[data-recently-viewed-products]', section);
    if (!container) return;

    const handles = RecentlyViewed.getExcluding(currentHandle);
    if (!handles.length) {
      section.style.display = 'none';
      return;
    }

    async function loadProducts() {
      try {
        const fetchPromises = handles.slice(0, 6).map(async (handle) => {
          try {
            const res = await fetch(`/products/${handle}.js`);
            if (res.ok) {
              return await res.json();
            }
          } catch { /* skip */ }
          return null;
        });

        const results = await Promise.all(fetchPromises);
        const validProducts = results.filter(Boolean);

        if (!validProducts.length) {
          section.style.display = 'none';
          return;
        }

        renderRecentlyViewed(validProducts);
      } catch (err) {
        console.error('Recently viewed error:', err);
        section.style.display = 'none';
      }
    }

    function renderRecentlyViewed(products) {
      container.innerHTML = products.map((p) => {
        const imgSrc = p.featured_image || (p.images && p.images[0] ? (p.images[0].src || p.images[0]) : '');
        const img = imgSrc
          ? `<img class="product-card__image" src="${imgSrc}" alt="${escapeHTML(p.title)}" loading="lazy">`
          : '';
        const price = p.price || (p.variants && p.variants[0] ? p.variants[0].price : 0);
        return `
          <div class="product-card product-card--small" data-product-card>
            <div class="product-card__media">
              <a href="/products/${p.handle}" class="product-card__link">${img}</a>
            </div>
            <div class="product-card__info">
              <a href="/products/${p.handle}" class="product-card__title">${escapeHTML(p.title)}</a>
              <p class="product-card__price">${formatMoney(price)}</p>
            </div>
          </div>
        `;
      }).join('');

      initProductCards(container);
    }

    loadProducts();
  }

  /* ==========================================================================
     24. COLLECTION PAGE FILTERING
     ========================================================================== */

  function initCollectionFilters() {
    const filterBar = $('[data-filter-bar]');
    let collectionGrid = $('#collection-grid');
    let paginationNav = $('.pagination');

    let isFiltering = false;

    // Apply filters: fetch filtered page and swap grid content
    function applyFilters(newUrl) {
      if (isFiltering) return;
      isFiltering = true;

      if (!collectionGrid) {
        window.location.href = newUrl;
        return;
      }

      // Show loading state
      collectionGrid.style.opacity = '0.5';

      // Fetch the full page and parse the grid from it
      fetch(newUrl)
        .then(function(res) {
          if (!res.ok) throw new Error('Filter fetch failed');
          return res.text();
        })
        .then(function(html) {
          // Parse response and extract updated elements
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newGrid = doc.querySelector('#collection-grid');

          if (!newGrid) {
            // section_id rendering didn't return expected HTML — full page reload
            window.location.href = newUrl;
            return;
          }

          const newPagination = doc.querySelector('.pagination');
          const newChips = doc.querySelector('#filter-chips');
          const newCount = doc.querySelector('.collection-header__count');

          collectionGrid.innerHTML = newGrid.innerHTML;
          initProductCards(collectionGrid);

          // Update filter chips
          const currentChips = $('#filter-chips');
          if (currentChips) {
            currentChips.innerHTML = newChips ? newChips.innerHTML : '';
          }

          // Update product count
          const currentCount = $('.collection-header__count');
          if (currentCount && newCount) currentCount.textContent = newCount.textContent;

          // Update pagination
          if (paginationNav) {
            if (newPagination) {
              paginationNav.innerHTML = newPagination.innerHTML;
              paginationNav.style.display = '';
            } else {
              paginationNav.style.display = 'none';
            }
          }

          // Update URL without reload
          history.pushState(null, '', newUrl);

          // Scroll to grid
          collectionGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
        .catch(function() {
          // Fallback to full page navigation
          window.location.href = newUrl;
        })
        .finally(function() {
          if (collectionGrid) collectionGrid.style.opacity = '';
          isFiltering = false;
        });
    }

    function buildFilterUrl(formEl) {
      var formData = new FormData(formEl);
      var url = new URL(window.location);

      // Clear all existing filter params
      [...url.searchParams.keys()].forEach(function(key) {
        if (key.startsWith('filter.')) url.searchParams.delete(key);
      });

      // Add checked/filled filter params (skip empty and default-zero price ranges)
      for (var pair of formData.entries()) {
        if (!pair[0].startsWith('filter.') || pair[1] === '') continue;

        // Skip price range inputs at their default (min=0, max=range_max)
        if (pair[0].endsWith('.gte') || pair[0].endsWith('.lte')) {
          var numVal = parseFloat(pair[1]);
          if (isNaN(numVal) || numVal === 0) continue;
        }

        url.searchParams.append(pair[0], pair[1]);
      }

      url.searchParams.delete('page');
      return url.toString();
    }

    // Sort — AJAX (sync all sort selects)
    $$('[data-sort-select]').forEach(function(sortSelect) {
      sortSelect.addEventListener('change', function() {
        // Sync all sort selects to the same value
        $$('[data-sort-select]').forEach(function(other) {
          if (other !== sortSelect) other.value = sortSelect.value;
        });
        var url = new URL(window.location);
        url.searchParams.set('sort_by', sortSelect.value);
        url.searchParams.delete('page');
        applyFilters(url.toString());
      });
    });

    // Desktop filter bar — trigger dropdowns
    if (filterBar) {
      function closeAllDropdowns() {
        $$('.filter-bar__dropdown.is-open', filterBar).forEach(function(d) {
          d.classList.remove('is-open');
        });
        $$('.filter-bar__trigger.is-open', filterBar).forEach(function(t) {
          t.classList.remove('is-open');
          t.setAttribute('aria-expanded', 'false');
        });
      }

      $$('.filter-bar__trigger', filterBar).forEach(function(trigger) {
        trigger.addEventListener('click', function(e) {
          e.preventDefault();
          var wrap = trigger.closest('.filter-bar__trigger-wrap');
          if (!wrap) return;
          var dropdown = wrap.querySelector('.filter-bar__dropdown');
          if (!dropdown) return;

          var isOpen = dropdown.classList.contains('is-open');
          closeAllDropdowns();

          if (!isOpen) {
            dropdown.classList.add('is-open');
            trigger.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });

      document.addEventListener('click', function(e) {
        if (!e.target.closest('.filter-bar__trigger-wrap')) {
          closeAllDropdowns();
        }
      });

      // Any filter input change — AJAX filter
      delegate(filterBar, '[data-filter-input]', 'change', function() {
        applyFilters(buildFilterUrl(filterBar));
      });
    }

    // Mobile filter drawer
    const filterDrawer = $('[data-filter-drawer]');
    const filterOpenBtn = $('[data-action="open-filters"]');

    function openFilterDrawer() {
      if (!filterDrawer) return;
      filterDrawer.classList.add('is-open');
      document.body.classList.add('no-scroll');
    }

    function closeFilterDrawer() {
      if (!filterDrawer) return;
      filterDrawer.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    }

    if (filterOpenBtn) {
      filterOpenBtn.addEventListener('click', openFilterDrawer);
    }

    if (filterDrawer) {
      $$('[data-filter-drawer-close]', filterDrawer).forEach(function(btn) {
        btn.addEventListener('click', closeFilterDrawer);
      });

      $$('[data-filter-accordion]', filterDrawer).forEach(function(btn) {
        btn.addEventListener('click', function() {
          var group = btn.closest('.filter-drawer__group');
          if (group) group.classList.toggle('is-open');
        });
      });

      var applyBtn = $('[data-filter-drawer-apply]', filterDrawer);
      if (applyBtn) {
        applyBtn.addEventListener('click', function() {
          var form = $('form', filterDrawer);
          if (!form) return;
          closeFilterDrawer();
          applyFilters(buildFilterUrl(form));
        });
      }
    }

    // Handle pagination clicks via AJAX too
    delegate(document, '.pagination a', 'click', function(e, link) {
      if (!collectionGrid) return; // fallback to normal nav
      e.preventDefault();
      applyFilters(link.href);
    });

    // Handle browser back/forward
    window.addEventListener('popstate', function() {
      applyFilters(window.location.href);
    });
  }

  /* ==========================================================================
     25. CART PAGE
     ========================================================================== */

  function initCartPage() {
    const cartForm = $('[data-cart-form]') || $('form[action="/cart"]');
    if (!cartForm) return;

    function updateCartPageUI(cart) {
      // Update header badge
      $$('[data-cart-count]').forEach(badge => {
        badge.textContent = cart.item_count;
        badge.style.display = cart.item_count > 0 ? '' : 'none';
      });

      // Update summary totals
      const subtotalEl = $('#cart-subtotal');
      const totalEl = $('#cart-total');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

      // Update item count header
      const countEl = $('.cart-page__items-count', cartForm);
      if (countEl) countEl.textContent = cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's');

      // Update line item prices
      cart.items.forEach(item => {
        const row = cartForm.querySelector('[data-variant-id="' + item.variant_id + '"]');
        if (!row) return;
        const priceEl = $('.cart-item__price', row);
        if (priceEl) {
          if (item.original_line_price !== item.final_line_price) {
            priceEl.innerHTML = '<span class="cart-item__price-sale">' + formatMoney(item.final_line_price) + '</span>' +
              '<span class="cart-item__price-compare">' + formatMoney(item.original_line_price) + '</span>';
          } else {
            priceEl.innerHTML = '<span>' + formatMoney(item.final_line_price) + '</span>';
          }
        }
      });

      // Update shipping bar
      const threshold = window.ARTFIT.freeShippingThreshold;
      if (threshold > 0) {
        const thresholdCents = threshold * 100;
        const remaining = thresholdCents - cart.total_price;
        const barEl = $('.cart-page__shipping-bar');
        if (barEl) {
          const msgEl = $('.cart-page__shipping-msg', barEl);
          const fillEl = $('.cart-page__shipping-progress-fill', barEl);
          if (remaining > 0) {
            barEl.classList.remove('cart-page__shipping-bar--complete');
            if (msgEl) msgEl.innerHTML = 'You\'re <strong>' + formatMoney(remaining) + '</strong> away from free shipping!';
            if (fillEl) fillEl.style.width = Math.round(cart.total_price / thresholdCents * 100) + '%';
          } else {
            barEl.classList.add('cart-page__shipping-bar--complete');
            if (msgEl) msgEl.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> You\'ve unlocked <strong>free shipping!</strong>';
            if (fillEl) fillEl.style.width = '100%';
          }
        }
      }

      // If cart is empty, replace with empty state
      if (cart.item_count === 0) {
        const page = $('.cart-page');
        if (page) {
          page.innerHTML = '<h1 class="cart-page__title">Your Bag</h1>' +
            '<div class="cart-empty">' +
            '<div class="cart-empty__icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" stroke-width="1.5"/><path d="M16 10a4 4 0 01-8 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>' +
            '<h2 class="cart-empty__title">Your bag is empty</h2>' +
            '<p class="cart-empty__text">Looks like you haven\'t added anything to your bag yet.</p>' +
            '<a href="/collections/all" class="btn btn--primary btn--lg">Start Shopping</a>' +
            '</div>';
        }
      }
    }

    // Quantity controls
    delegate(cartForm, '[data-cart-qty-change]', 'click', async (e, btn) => {
      e.preventDefault();
      const key = btn.dataset.lineKey;
      const change = parseInt(btn.dataset.cartQtyChange, 10);
      const itemRow = btn.closest('[data-cart-line-item]');
      const qtyInput = itemRow ? $('[data-cart-qty-input]', itemRow) || $('input[type="number"]', itemRow) : null;
      const current = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      const newQty = Math.max(0, current + change);

      // Optimistic update
      if (qtyInput) qtyInput.value = newQty;
      btn.disabled = true;

      try {
        const cart = await CartAPI.changeItem(key, newQty);
        if (newQty === 0 && itemRow) {
          itemRow.style.transition = 'opacity 0.3s, transform 0.3s';
          itemRow.style.opacity = '0';
          itemRow.style.transform = 'translateX(-20px)';
          setTimeout(() => itemRow.remove(), 300);
        }
        updateCartPageUI(cart);
      } catch (err) {
        console.error('Cart qty error:', err);
        if (qtyInput) qtyInput.value = current;
      }
      btn.disabled = false;
    });

    // Remove
    delegate(cartForm, '[data-cart-remove]', 'click', async (e, btn) => {
      e.preventDefault();
      const key = btn.dataset.lineKey;
      const itemRow = btn.closest('[data-cart-line-item]');

      // Optimistic removal animation
      if (itemRow) {
        itemRow.style.transition = 'opacity 0.3s, transform 0.3s';
        itemRow.style.opacity = '0';
        itemRow.style.transform = 'translateX(-20px)';
      }

      try {
        const cart = await CartAPI.removeItem(key);
        if (itemRow) setTimeout(() => itemRow.remove(), 300);
        updateCartPageUI(cart);
      } catch (err) {
        console.error('Cart remove error:', err);
        if (itemRow) {
          itemRow.style.opacity = '1';
          itemRow.style.transform = '';
        }
      }
    });

    // Inline qty input change
    delegate(cartForm, '[data-cart-qty-input]', 'change', async (e, input) => {
      const key = input.dataset.lineKey;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      const itemRow = input.closest('[data-cart-line-item]');

      try {
        const cart = await CartAPI.changeItem(key, qty);
        if (qty === 0 && itemRow) {
          itemRow.style.transition = 'opacity 0.3s, transform 0.3s';
          itemRow.style.opacity = '0';
          itemRow.style.transform = 'translateX(-20px)';
          setTimeout(() => itemRow.remove(), 300);
        }
        updateCartPageUI(cart);
      } catch (err) {
        console.error('Cart input error:', err);
      }
    });

    // Cart note
    const noteInput = $('[data-cart-note]', cartForm);
    if (noteInput) {
      noteInput.addEventListener('change', debounce(() => {
        CartAPI.updateNote(noteInput.value);
      }, 500));
    }
  }

  /* ==========================================================================
     26. SEARCH PAGE
     ========================================================================== */

  function initSearchPage() {
    const searchForm = $('[data-search-page-form]') || $('form[action="/search"]');
    if (!searchForm) return;

    const input = $('input[name="q"]', searchForm);
    if (!input) return;

    // Focus input
    if (!input.value) input.focus();

    // Clear search button
    delegate(searchForm, '[data-action="clear-search"]', 'click', () => {
      input.value = '';
      input.focus();
      const clearBtn = $('[data-action="clear-search"]', searchForm);
      if (clearBtn) clearBtn.style.display = 'none';
    });

    // Search suggestions under the input
    const suggestionsContainer = $('[data-search-suggestions]');
    if (!suggestionsContainer) return;

    const performSuggestions = debounce(async (query) => {
      if (!query || query.length < 2) {
        suggestionsContainer.innerHTML = '';
        suggestionsContainer.style.display = 'none';
        return;
      }

      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`
        );
        if (!res.ok) return;
        const data = await res.json();
        const products = data.resources?.results?.products || [];

        if (!products.length) {
          suggestionsContainer.style.display = 'none';
          return;
        }

        suggestionsContainer.style.display = '';
        suggestionsContainer.innerHTML = products.map((p) => `
          <a href="${p.url}" class="search-suggestion">
            ${p.image ? `<img src="${p.image.replace(/(\.[^.]+)$/, '_80x$1')}" alt="" width="40" height="50" loading="lazy">` : ''}
            <span>${escapeHTML(p.title)}</span>
          </a>
        `).join('');
      } catch { /* ignore */ }
    }, 300);

    input.addEventListener('input', () => performSuggestions(input.value.trim()));

    // Close suggestions on click outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-search-page-form]') && suggestionsContainer) {
        suggestionsContainer.style.display = 'none';
      }
    });
  }

  /* ==========================================================================
     27. SIZE GUIDE
     ========================================================================== */

  function initSizeGuide() {
    // Move Kiwi's size chart button into the Size label row
    function relocateKiwi() {
      var labelRow = document.querySelector('[data-option-name="Size"] .variant-selector__label-row');
      var kiwiContainer = document.querySelector('.pdp__info .ks-chart-container');
      if (labelRow && kiwiContainer && !labelRow.contains(kiwiContainer)) {
        labelRow.appendChild(kiwiContainer);
      }
    }
    // Kiwi loads async — observe for it
    var observer = new MutationObserver(function() {
      var kiwiContainer = document.querySelector('.pdp__info .ks-chart-container');
      if (kiwiContainer) {
        relocateKiwi();
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // Also try immediately in case it's already there
    relocateKiwi();
  }

  /* ==========================================================================
     28. LAZY LOAD
     ========================================================================== */

  function initLazyLoad() {
    if ('loading' in HTMLImageElement.prototype) {
      // Browser supports native lazy loading, handle data-src swaps only
      $$('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
      });
      return;
    }

    // Fallback for older browsers
    if (!('IntersectionObserver' in window)) {
      $$('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              img.removeAttribute('data-srcset');
            }
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: '200px' }
    );

    $$('img[data-src]').forEach((img) => observer.observe(img));
  }

  /* ==========================================================================
     29. PRODUCT FORM (PDP) - Add to Cart
     ========================================================================== */

  function initProductForm() {
    const forms = $$('form[data-product-form]');
    forms.forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = $('[data-add-to-cart]', form) || $('button[type="submit"]', form);
        if (!submitBtn || submitBtn.disabled) return;

        const variantInput = $('input[name="id"]', form);
        if (!variantInput || !variantInput.value) {
          // Scroll to variant selector
          const selector = $('[data-variant-selector]');
          if (selector) {
            selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
            selector.classList.add('shake');
            setTimeout(() => selector.classList.remove('shake'), 600);
          }
          return;
        }

        const variantId = parseInt(variantInput.value, 10);
        const qtyInput = $('input[name="quantity"]', form);
        const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        // Collect properties
        const properties = {};
        $$('input[name^="properties["]', form).forEach((input) => {
          const name = input.name.match(/properties\[(.+?)\]/)?.[1];
          if (name && input.value) properties[name] = input.value;
        });
        if (submitBtn.dataset.preorder) properties['_preorder'] = 'true';

        submitBtn.classList.add('is-loading');
        submitBtn.disabled = true;
        const textEl = $('[data-add-to-cart-text]', submitBtn) || submitBtn;
        const originalText = textEl.textContent;

        try {
          await CartAPI.addItem(variantId, quantity, properties);
          textEl.textContent = submitBtn.dataset.preorder ? 'Pre-Ordered!' : 'Added to Cart!';
          document.dispatchEvent(new CustomEvent('cart:item-added'));

          setTimeout(() => {
            textEl.textContent = originalText;
            submitBtn.classList.remove('is-loading');
            submitBtn.disabled = false;
          }, 2000);
        } catch (err) {
          console.error('Add to cart error:', err);
          textEl.textContent = err.message || 'Error - Try Again';
          setTimeout(() => {
            textEl.textContent = originalText;
            submitBtn.classList.remove('is-loading');
            submitBtn.disabled = false;
          }, 3000);
        }
      });
    });
  }

  /* ==========================================================================
     30. ANNOUNCEMENT BAR
     ========================================================================== */


  /* ==========================================================================
     INITIALIZE
     ========================================================================== */

  document.addEventListener('DOMContentLoaded', () => {
    // Critical — visible above the fold
    initHeader();
    initMegaMenu();
    initHero();
    initProductSliders();
    initProductCards();
    initCartDrawer();
    initMobileBottomNav();
    initLazyLoad();
    initProductForm();
    initQuickView();

    // Deferred — below the fold or user-initiated
    const deferInit = window.requestIdleCallback || ((cb) => setTimeout(cb, 80));
    deferInit(() => {
      initScrollReveal();
      initSearchDrawer();
      initContactModal();
      initThemeSwitcher();
      initAccordions();
    });

    // Wishlist: sync with account (if logged in) then update UI
    Wishlist.updateUI();
    Wishlist.syncWithAccount();

    // Page-specific initialization
    const page = document.body.dataset.page;

    if (page === 'product') {
      initVariantSelector();
      initProductGallery();
      initStickyAddToCart();
      initCompleteTheLook();
      initProductRecommendations();
      initRecentlyViewed();
      initSizeGuide();

      // Pickup toggle
      delegate(document, '[data-pickup-toggle]', 'click', (e, btn) => {
        const details = btn.nextElementSibling;
        if (details) {
          const isOpen = details.classList.toggle('is-open');
          btn.textContent = isOpen ? 'Hide store information' : 'View store information';
        }
      });
    }

    if (page === 'collection') {
      initCollectionFilters();
    }

    if (page === 'cart') {
      initCartPage();
    }

    if (page === 'search') {
      initSearchPage();
    }

    if ($('[data-wishlist-page]')) {
      initWishlistPage();
    }
  });

  /* ==========================================================================
     WISHLIST PAGE
     ========================================================================== */

  function initWishlistPage() {
    const grid = $('[data-wishlist-grid]');
    const emptyState = $('[data-wishlist-empty]');
    const countEl = $('[data-wishlist-page-count]');
    if (!grid) return;

    const ids = Wishlist.getAll();

    function updateCount(count) {
      if (countEl) countEl.textContent = count + ' item' + (count === 1 ? '' : 's');
    }

    updateCount(ids.length);

    if (!ids.length) {
      grid.style.display = 'none';
      emptyState.style.display = '';
      return;
    }

    grid.innerHTML = '<p class="wishlist-page__loading">Loading your wishlist...</p>';

    // Fetch each product individually via /products/{handle}.js
    // Items with handles use direct fetch; legacy items (ID-only) use search fallback
    var items = Wishlist.getAll();

    Promise.all(items.map(function(item) {
      if (item.handle) {
        return fetch('/products/' + item.handle + '.js')
          .then(function(r) { return r.ok ? r.json() : null; })
          .catch(function() { return null; });
      }
      // Legacy items without handle — skip (will be cleaned up naturally as user re-adds)
      return Promise.resolve(null);
    }))
    .then(function(results) {
      var products = results.filter(Boolean);

      if (!products.length) {
        grid.style.display = 'none';
        emptyState.style.display = '';
        updateCount(0);
        return;
      }

      var html = '';
      products.forEach(function(p) {
        var image = p.featured_image ? p.featured_image : (p.images && p.images.length ? p.images[0] : '');
        var hoverImage = p.images && p.images.length > 1 ? p.images[1] : '';
        var variant = p.variants && p.variants[0];
        var price = variant ? formatMoney(variant.price) : '';
        var comparePrice = variant && variant.compare_at_price ? formatMoney(variant.compare_at_price) : '';
        var onSale = variant && variant.compare_at_price && variant.compare_at_price > variant.price;
        var available = p.available;
        var hasMultipleVariants = p.variants && p.variants.length > 1;

        html += '<article class="product-card" data-product-id="' + p.id + '">';
        html += '<div class="product-card__media">';
        html += '<a href="/products/' + p.handle + '" class="product-card__link">';
        if (image) {
          html += '<img class="product-card__image product-card__image--primary" src="' + escapeHTML(image) + '" alt="' + escapeHTML(p.title) + '" loading="lazy">';
          if (hoverImage) html += '<img class="product-card__image product-card__image--hover" src="' + escapeHTML(hoverImage) + '" alt="" loading="lazy">';
        }
        html += '</a>';
        html += '<div class="product-card__badges">';
        if (!available) html += '<span class="badge badge--sold-out">Sold Out</span>';
        else if (onSale) html += '<span class="badge badge--sale">-' + Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100) + '%</span>';
        html += '</div>';
        html += '<button class="product-card__wishlist is-wishlisted" type="button" data-wishlist-toggle data-product-id="' + p.id + '" data-product-handle="' + p.handle + '" aria-label="Remove from wishlist" aria-pressed="true">';
        html += '<svg class="filled" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>';
        html += '</button>';
        html += '</div>';
        html += '<div class="product-card__info">';
        html += '<a href="/products/' + p.handle + '" class="product-card__title-link"><h3 class="product-card__title">' + escapeHTML(p.title) + '</h3></a>';
        html += '<div class="price">';
        if (onSale) {
          html += '<span class="price__sale">' + price + '</span> <span class="price__compare">' + comparePrice + '</span>';
        } else {
          html += '<span class="price__regular">' + price + '</span>';
        }
        html += '</div>';
        if (available && variant) {
          if (hasMultipleVariants) {
            html += '<button class="product-card__quick-add" type="button" data-action="quick-add" data-product-id="' + p.id + '" data-product-handle="' + p.handle + '" data-variant-id="' + variant.id + '" data-multiple-variants="true">Select Options</button>';
          } else {
            html += '<button class="product-card__quick-add" type="button" data-action="quick-add" data-product-id="' + p.id + '" data-product-handle="' + p.handle + '" data-variant-id="' + variant.id + '">Add to Bag</button>';
          }
        }
        html += '</div></article>';
      });

      grid.innerHTML = html;
      updateCount(products.length);
      initProductCards(grid);

      // Remove card on wishlist toggle
      delegate(grid, '[data-wishlist-toggle]', 'click', function(e, btn) {
        e.preventDefault();
        e.stopPropagation();
        var pid = btn.dataset.productId;
        if (pid) Wishlist.toggle(pid);
        var card = btn.closest('.product-card');
        if (card) {
          card.style.transition = 'opacity 0.3s, transform 0.3s';
          card.style.opacity = '0';
          card.style.transform = 'scale(0.95)';
          setTimeout(function() {
            card.remove();
            var remaining = $$('.product-card', grid);
            if (!remaining.length) {
              grid.style.display = 'none';
              emptyState.style.display = '';
            }
            updateCount(remaining.length);
          }, 300);
        }
      });
    })
    .catch(function() {
      grid.innerHTML = '<p>Could not load wishlist. Please try again.</p>';
    });
  }

  /* ==========================================================================
     GIFT CARD PAGE
     ========================================================================== */

  (function initGiftCardPage() {
    var form = document.getElementById('gift-card-form');
    if (!form) return;

    // Read config lazily to ensure inline <script> in section has executed
    function getConfig() {
      return (window.ARTFIT && window.ARTFIT.giftCard) || {};
    }

    var amountInput = $('#gift-card-amount');
    var qtyInput = $('#gift-card-qty');
    var presets = $$('.gc-presets__btn');
    var recipientsContainer = $('#gc-recipients');
    var addRecipientBtn = $('#gc-add-recipient');
    var submitBtn = $('#gc-submit');
    var summaryValue = $('#gc-summary-value');
    var summaryQty = $('#gc-summary-qty');
    var summaryTotal = $('#gc-summary-total');
    var recipientCount = 1;

    // Live card preview elements
    var cardAmount = form.querySelector('.gc-card__value');
    var cardCurrency = form.querySelector('.gc-card__currency');
    var cardTo = $('#gc-card-to');
    var cardFrom = $('#gc-card-from');

    function formatAmount(val) {
      var config = getConfig();
      return (config.currency || '') + ' ' + Number(val || 0).toLocaleString();
    }

    function updateCardPreview() {
      var amount = parseInt(amountInput.value) || 0;
      if (cardAmount) cardAmount.textContent = Number(amount).toLocaleString();
      if (cardCurrency) cardCurrency.textContent = getConfig().currency || '';

      // Update "To" from first recipient's first name
      var firstRecipient = recipientsContainer ? recipientsContainer.querySelector('[data-field="fname"]') : null;
      if (cardTo && firstRecipient) {
        cardTo.textContent = firstRecipient.value.trim() || 'Someone special';
      }

      // Update "From" from sender's first name
      var senderFname = $('#gc-sender-fname');
      if (cardFrom && senderFname) {
        cardFrom.textContent = senderFname.value.trim() || 'You';
      }
    }

    function updateSummary() {
      var amount = parseInt(amountInput.value) || 0;
      var qty = parseInt(qtyInput.value) || 1;
      summaryValue.textContent = formatAmount(amount);
      summaryQty.textContent = qty;
      summaryTotal.textContent = formatAmount(amount * qty);
      updateCardPreview();
    }

    // Preset buttons
    presets.forEach(function(btn) {
      btn.addEventListener('click', function() {
        presets.forEach(function(b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        amountInput.value = btn.dataset.value;
        updateSummary();
      });
    });

    amountInput.addEventListener('input', function() {
      presets.forEach(function(b) { b.classList.remove('is-active'); });
      var matching = Array.from(presets).find(function(b) { return b.dataset.value === amountInput.value; });
      if (matching) matching.classList.add('is-active');
      updateSummary();
    });

    qtyInput.addEventListener('input', updateSummary);

    // Live preview: update To/From as user types
    form.addEventListener('input', function(e) {
      if (e.target.matches('[data-field="fname"]') || e.target.matches('#gc-sender-fname')) {
        updateCardPreview();
      }
    });

    // Personal message toggle
    form.addEventListener('change', function(e) {
      if (e.target.hasAttribute('data-toggle-message')) {
        var textarea = e.target.closest('.gc-recipient').querySelector('.gc-field__textarea');
        if (textarea) {
          textarea.classList.toggle('gc-field__textarea--hidden', !e.target.checked);
          if (e.target.checked) textarea.focus();
        }
      }
    });

    // Add recipient
    if (addRecipientBtn) {
      addRecipientBtn.addEventListener('click', function() {
        if (recipientCount >= 5) return;
        var idx = recipientCount++;
        var block = document.createElement('div');
        block.className = 'gc-recipient';
        block.dataset.recipient = idx;
        block.innerHTML =
          '<div class="gc-recipient__header">' +
          '<h4 class="gc-recipient__title">Recipient ' + (idx + 1) + '</h4>' +
          '<button type="button" class="gc-recipient__remove" data-remove-recipient>Remove</button>' +
          '</div>' +
          '<div class="gc-field-row">' +
          '<div class="gc-field"><label class="gc-field__label">First name</label>' +
          '<input type="text" class="gc-field__input" data-field="fname" placeholder="Recipient first name" required></div>' +
          '<div class="gc-field"><label class="gc-field__label">Last name</label>' +
          '<input type="text" class="gc-field__input" data-field="lname" placeholder="Recipient last name" required></div>' +
          '</div>' +
          '<div class="gc-field"><label class="gc-field__label">Email</label>' +
          '<input type="email" class="gc-field__input" data-field="email" placeholder="Recipient email" required></div>' +
          '<div class="gc-field"><label class="gc-field__label">Phone</label>' +
          '<input type="tel" class="gc-field__input" data-field="phone" placeholder="+254 7XX XXX XXX"></div>' +
          '<label class="gc-toggle">' +
          '<input type="checkbox" class="gc-toggle__check" data-toggle-message>' +
          '<span class="gc-toggle__switch"></span>' +
          '<span class="gc-toggle__text">Add a personal message</span></label>' +
          '<textarea class="gc-field__textarea gc-field__textarea--hidden" data-field="message" placeholder="Write something heartfelt..." rows="3" maxlength="200"></textarea>';
        recipientsContainer.appendChild(block);
        if (recipientCount >= 5) addRecipientBtn.style.display = 'none';
      });
    }

    // Remove recipient
    form.addEventListener('click', function(e) {
      var removeBtn = e.target.closest('[data-remove-recipient]');
      if (removeBtn) {
        removeBtn.closest('.gc-recipient').remove();
        recipientCount--;
        if (addRecipientBtn) addRecipientBtn.style.display = '';
        updateCardPreview();
      }
    });

    // Find the best matching variant for a given amount
    // Returns { variant, cartQty } — cartQty may differ from user qty for custom amounts
    function resolveVariant(amount, userQty) {
      var config = getConfig();
      if (!config.variants || !config.variants.length) return null;

      // Shopify stores prices in cents
      var amountCents = amount * 100;

      // 1. Try exact match first (preset amounts)
      var exact = config.variants.find(function(v) { return v.price === amountCents; });
      if (exact) return { variant: exact, cartQty: userQty, isCustom: false };

      // 2. For custom amounts, find the smallest variant (base unit, e.g. KES 100)
      var sorted = config.variants.slice().sort(function(a, b) { return a.price - b.price; });
      var base = sorted[0];
      if (!base || base.price === 0) return null;

      // Calculate how many units of the base variant = the custom amount
      var basePrice = base.price / 100; // convert cents to currency
      var units = Math.round(amount / basePrice);
      if (units < 1) return null;

      return { variant: base, cartQty: units * userQty, isCustom: true };
    }

    // Add gift card to Shopify cart with line item properties
    function addToCart(variantId, qty, properties) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: qty,
            properties: properties
          }]
        })
      }).then(function(res) {
        if (!res.ok) throw new Error('Could not add gift card to cart');
        return res.json();
      });
    }

    // Submit — add gift card to cart and redirect to Shopify checkout
    if (submitBtn) {
      submitBtn.addEventListener('click', async function() {
        var config = getConfig();
        var amount = parseInt(amountInput.value) || 0;
        var qty = parseInt(qtyInput.value) || 1;

        if (amount < config.minValue) {
          alert('Minimum gift card value is ' + formatAmount(config.minValue));
          amountInput.focus();
          return;
        }
        if (amount > config.maxValue) {
          alert('Maximum gift card value is ' + formatAmount(config.maxValue));
          amountInput.focus();
          return;
        }

        // Check gift card product is configured
        if (!config.productId) {
          alert('Gift card product is not configured. Please contact the store owner.');
          return;
        }

        // Gather sender info
        var senderFname = ($('#gc-sender-fname') || {}).value || '';
        var senderLname = ($('#gc-sender-lname') || {}).value || '';
        var senderEmail = ($('#gc-sender-email') || {}).value || '';
        var senderPhone = ($('#gc-sender-phone') || {}).value || '';

        if (!senderFname.trim() || !senderEmail.trim()) {
          alert('Please fill in your name and email.');
          return;
        }

        // Gather recipients
        var recipientBlocks = $$('.gc-recipient', recipientsContainer);
        var recipients = [];
        var valid = true;

        recipientBlocks.forEach(function(block) {
          var fname = (block.querySelector('[data-field="fname"]') || {}).value || '';
          var lname = (block.querySelector('[data-field="lname"]') || {}).value || '';
          var email = (block.querySelector('[data-field="email"]') || {}).value || '';
          var phone = (block.querySelector('[data-field="phone"]') || {}).value || '';
          var message = (block.querySelector('[data-field="message"]') || {}).value || '';

          if (!fname.trim() || !email.trim()) {
            valid = false;
            return;
          }
          recipients.push({ fname: fname.trim(), lname: lname.trim(), email: email.trim(), phone: phone.trim(), message: message.trim() });
        });

        if (!valid || recipients.length === 0) {
          alert('Please fill in all required recipient fields (name and email).');
          return;
        }

        // Resolve variant — handles both preset and custom amounts
        var resolved = resolveVariant(amount, qty);
        if (!resolved) {
          alert('Could not find a matching gift card option. Please try a different amount or contact the store.');
          return;
        }

        // Validate custom amounts are divisible by base unit
        if (resolved.isCustom) {
          var basePrice = resolved.variant.price / 100;
          if (amount % basePrice !== 0) {
            alert('Custom amount must be a multiple of ' + formatAmount(basePrice) + '.');
            amountInput.focus();
            return;
          }
        }

        submitBtn.disabled = true;
        submitBtn.querySelector('.gc-checkout__btn-text').textContent = 'Adding to cart...';

        try {
          // Build line item properties for each recipient
          for (var i = 0; i < recipients.length; i++) {
            var r = recipients[i];
            var properties = {
              '__shopify_send_gift_card_to_recipient': 'true',
              'Recipient email': r.email,
              'Recipient name': r.fname + ' ' + r.lname,
              'Message': r.message || '',
              '_Sender Name': senderFname.trim() + ' ' + senderLname.trim(),
              '_Sender Email': senderEmail.trim(),
              '_Sender Phone': senderPhone.trim() || '',
              'Recipient Phone': r.phone || '',
              'Gift Card Value': config.currency + ' ' + Number(amount).toLocaleString()
            };

            await addToCart(resolved.variant.id, resolved.cartQty, properties);
          }

          // Redirect to Shopify checkout
          window.location.href = '/checkout';

        } catch (err) {
          alert(err.message || 'Something went wrong. Please try again.');
          submitBtn.disabled = false;
          submitBtn.querySelector('.gc-checkout__btn-text').textContent = 'Purchase & Send';
        }
      });
    }

    // Restore button state when navigating back from checkout
    window.addEventListener('pageshow', function(e) {
      if (e.persisted && submitBtn) {
        submitBtn.disabled = false;
        submitBtn.querySelector('.gc-checkout__btn-text').textContent = 'Purchase & Send';
      }
    });

    updateSummary();
  })();

  // Expose key objects globally
  window.ARTFIT = window.ARTFIT || {};
  window.ARTFIT.CartAPI = CartAPI;
  window.ARTFIT.Wishlist = Wishlist;
  window.ARTFIT.RecentlyViewed = RecentlyViewed;
  window.ARTFIT.formatMoney = formatMoney;

})();
